/**
 * MYD native cart drawer helpers (quantity updates, refresh, coupons, OOS + wishlist).
 * Only active when cart_drawer_engine == myd (script gated in theme.liquid).
 */
(function () {
  'use strict';

  const SELECTOR = {
    root: '[data-myd-cart]',
    sheet: '[data-myd-sheet]',
    removeAll: '[data-myd-remove-all]',
    oosRemove: '[data-myd-oos-remove], [data-myd-line-remove]',
    sheetClose: '[data-myd-sheet-close]',
    sheetDelete: '[data-myd-sheet-delete]',
    sheetWishlist: '[data-myd-sheet-wishlist]',
    oosLines: '[data-myd-oos] .myd-cart__line',
  };

  let pending = null;
  let couponActionPending = false;
  let couponLockTimer = null;
  let couponActionWatchdog = null;

  function setCouponDrawerLock(on) {
    document.body.classList.toggle('myd-cart-coupon-busy', !!on);
  }

  function beginCouponAction(control) {
    if (couponActionPending) return false;
    couponActionPending = true;
    if (couponLockTimer) {
      clearTimeout(couponLockTimer);
      couponLockTimer = null;
    }
    if (couponActionWatchdog) {
      clearTimeout(couponActionWatchdog);
      couponActionWatchdog = null;
    }
    setCouponDrawerLock(true);
    // Safety: never leave Apply/Remove stuck spinning if a fetch hangs.
    couponActionWatchdog = setTimeout(() => {
      couponActionWatchdog = null;
      if (couponActionPending) endCouponAction(control);
    }, 8000);
    if (!control) return true;
    control.dataset.mydCouponOriginalHtml = control.innerHTML;
    control.classList.add('is-loading');
    control.disabled = true;
    control.setAttribute('aria-busy', 'true');
    control.innerHTML = '<span class="myd-cart__coupon-loader" aria-hidden="true"></span>';
    return true;
  }

  function endCouponAction(control) {
    couponActionPending = false;
    if (couponActionWatchdog) {
      clearTimeout(couponActionWatchdog);
      couponActionWatchdog = null;
    }
    if (control) {
      if (control.dataset.mydCouponOriginalHtml != null) {
        control.innerHTML = control.dataset.mydCouponOriginalHtml;
        delete control.dataset.mydCouponOriginalHtml;
      }
      control.classList.remove('is-loading');
      control.disabled = false;
      control.removeAttribute('aria-busy');
    }
    // Hold the lock past any in-flight close animation / ghost click (theme close is 400ms)
    if (couponLockTimer) clearTimeout(couponLockTimer);
    couponLockTimer = setTimeout(() => {
      couponLockTimer = null;
      setCouponDrawerLock(false);
    }, 500);
  }

  function root() {
    return document.querySelector(SELECTOR.root);
  }

  function sheet() {
    const r = root();
    return r ? r.querySelector(SELECTOR.sheet) : null;
  }

  function clearSheetDataset(s) {
    if (!s) return;
    delete s.dataset.pendingKey;
    delete s.dataset.pendingIndex;
    delete s.dataset.pendingVariantId;
    delete s.dataset.pendingProductId;
    delete s.dataset.pendingTitle;
  }

  function openSheet(payload) {
    pending = payload || null;
    const s = sheet();
    if (!s) return;
    // Persist on the DOM so a mid-flight section re-render cannot leave a
    // visible dialog with nothing bound to Delete.
    if (pending) {
      if (pending.key != null && pending.key !== '') s.dataset.pendingKey = String(pending.key);
      else delete s.dataset.pendingKey;
      if (Number.isFinite(Number(pending.index))) s.dataset.pendingIndex = String(pending.index);
      else delete s.dataset.pendingIndex;
      if (pending.variantId != null && pending.variantId !== '') {
        s.dataset.pendingVariantId = String(pending.variantId);
      } else delete s.dataset.pendingVariantId;
      if (pending.productId != null && pending.productId !== '') {
        s.dataset.pendingProductId = String(pending.productId);
      } else delete s.dataset.pendingProductId;
      if (pending.title) s.dataset.pendingTitle = String(pending.title);
      else delete s.dataset.pendingTitle;
    } else {
      clearSheetDataset(s);
    }
    const img = s.querySelector('[data-myd-sheet-img]');
    const title = s.querySelector('[data-myd-sheet-title]');
    const meta = s.querySelector('[data-myd-sheet-meta]');
    if (img) {
      if (payload?.image) {
        img.src = payload.image;
        img.hidden = false;
      } else {
        img.removeAttribute('src');
        img.hidden = true;
      }
    }
    if (title) title.textContent = payload?.title || '';
    if (meta) {
      const bits = [payload?.size, payload?.price].filter(Boolean);
      meta.textContent = bits.join(' · ');
    }
    s.hidden = false;
  }

  function closeSheet() {
    pending = null;
    const s = sheet();
    if (!s) return;
    clearSheetDataset(s);
    s.hidden = true;
  }

  /** Recover delete/wishlist target if in-memory pending was wiped mid-refresh. */
  function resolvePending() {
    if (pending?.key || Number.isFinite(Number(pending?.index))) return pending;
    const s = sheet();
    const r = root();
    if (!s || s.hidden) return null;

    const fromDom = {
      key: s.dataset.pendingKey || '',
      index: s.dataset.pendingIndex != null ? Number(s.dataset.pendingIndex) : NaN,
      variantId: s.dataset.pendingVariantId || '',
      productId: s.dataset.pendingProductId || '',
      title:
        s.dataset.pendingTitle ||
        s.querySelector('[data-myd-sheet-title]')?.textContent?.trim() ||
        '',
    };

    let line = null;
    if (fromDom.key && r) {
      line = [...r.querySelectorAll('.myd-cart__line')].find(
        (el) => el.getAttribute('data-key') === fromDom.key
      );
    }
    if (!line && Number.isFinite(fromDom.index) && r) {
      line = r.querySelector(`.myd-cart__line[data-index="${fromDom.index}"]`);
    }
    if (!line && fromDom.variantId && r) {
      line = r.querySelector(`.myd-cart__line[data-variant-id="${fromDom.variantId}"]`);
    }
    if (!line && fromDom.title && r) {
      line = [...r.querySelectorAll('.myd-cart__line')].find(
        (el) => (el.dataset.productTitle || '') === fromDom.title
      );
    }

    if (line) {
      const nextIndex = Number(line.dataset.index);
      pending = {
        key: line.dataset.key || fromDom.key,
        index: Number.isFinite(nextIndex) ? nextIndex : fromDom.index,
        variantId: line.dataset.variantId || fromDom.variantId,
        productId: line.dataset.productId || fromDom.productId,
        handle: line.dataset.productHandle || '',
        title: line.dataset.productTitle || fromDom.title,
        image: line.dataset.productImage || '',
        size: line.dataset.productSize || '',
        price: line.dataset.productPrice || '',
      };
      return pending;
    }

    if (fromDom.key || Number.isFinite(fromDom.index)) {
      pending = fromDom;
      return pending;
    }
    return null;
  }

  // A cart re-render swaps #mini-cart innerHTML, destroying an open confirm
  // dialog. Repaint it on the fresh DOM so the pending line can still be acted on.
  function restoreSheetAfterRender() {
    if (!pending) return;
    const r = root();
    if (!r) return;

    let line = null;
    if (pending.key) {
      line = [...r.querySelectorAll('.myd-cart__line')].find(
        (el) => el.getAttribute('data-key') === String(pending.key)
      );
    }
    if (!line && Number.isFinite(Number(pending.index))) {
      line = r.querySelector(`.myd-cart__line[data-index="${pending.index}"]`);
    }
    if (!line && pending.variantId) {
      line = r.querySelector(`.myd-cart__line[data-variant-id="${pending.variantId}"]`);
    }
    if (!line && pending.title) {
      line = [...r.querySelectorAll('.myd-cart__line')].find(
        (el) => (el.dataset.productTitle || '') === pending.title
      );
    }
    if (!line) {
      // Don't wipe pending — Delete can still resolve via key/index/title.
      // Re-show the fresh sheet shell so the dialog stays actionable.
      if (sheet()) openSheet(pending);
      return;
    }

    const nextIndex = Number(line.dataset.index);
    pending = {
      ...pending,
      key: line.dataset.key || pending.key,
      index: Number.isFinite(nextIndex) ? nextIndex : pending.index,
      variantId: line.dataset.variantId || pending.variantId,
      productId: line.dataset.productId || pending.productId,
      title: line.dataset.productTitle || pending.title,
      image: line.dataset.productImage || pending.image,
      size: line.dataset.productSize || pending.size,
      price: line.dataset.productPrice || pending.price,
    };
    openSheet(pending);
  }

  function sectionsToRequest() {
    const mini = document.querySelector('mini-cart');
    let list = [];
    if (mini && typeof mini.getSectionsToRender === 'function') {
      list = mini.getSectionsToRender().map((s) => s.section || s.id);
    } else {
      list = [mini?.dataset?.section || 'mini-cart', 'cart-icon-bubble'];
    }
    return [...new Set(list.filter((id) => {
      if (!id || id === 'undefined' || id === 'null') return false;
      // This group often isn't in the theme and 400s cart section renders
      if (id === 'mobile-cart-icon-bubble') return false;
      if (id === 'cart-live-region-text' && !document.getElementById('cart-live-region-text')) {
        return false;
      }
      if (id === 'main-cart-items' && !document.getElementById('main-cart-items')) {
        return false;
      }
      if (id === 'main-cart-footer' && !document.getElementById('main-cart-footer')) {
        return false;
      }
      return true;
    }))];
  }

  let quantityUpdateLock = false;
  let cartBusyDepth = 0;
  let cartBusySafetyTimer = null;
  const CART_BUSY_MAX_MS = 60 * 1000;

  function busyOverlay() {
    return root()?.querySelector('[data-myd-busy]');
  }

  function clearCartBusySafetyTimer() {
    if (cartBusySafetyTimer) {
      clearTimeout(cartBusySafetyTimer);
      cartBusySafetyTimer = null;
    }
  }

  function forceHideMydCartBusy() {
    cartBusyDepth = 0;
    clearCartBusySafetyTimer();
    const overlay = busyOverlay();
    if (overlay) {
      overlay.classList.add('hidden');
      overlay.setAttribute('hidden', '');
      overlay.setAttribute('aria-hidden', 'true');
    }
    root()?.classList.remove('is-busy');
    // If Nector apply/remove was stuck behind the overlay, unlock the button too.
    if (typeof nectorPendingForceApply !== 'undefined' && nectorPendingForceApply) {
      nectorPendingForceApply = false;
    }
    if (typeof nectorPendingRemove !== 'undefined' && nectorPendingRemove) {
      nectorPendingRemove = false;
    }
    if (
      (typeof nectorPendingForceApply !== 'undefined' || typeof nectorPendingRemove !== 'undefined') &&
      typeof wireNectorApplyButton === 'function'
    ) {
      const host = root()?.querySelector('[data-myd-nector-host]');
      if (host) {
        try {
          wireNectorApplyButton(host);
        } catch (_) {
          /* ignore */
        }
      }
    }
  }

  function armCartBusySafetyTimer() {
    clearCartBusySafetyTimer();
    cartBusySafetyTimer = window.setTimeout(() => {
      cartBusySafetyTimer = null;
      forceHideMydCartBusy();
    }, CART_BUSY_MAX_MS);
  }

  function showMydCartBusy() {
    cartBusyDepth += 1;
    const overlay = busyOverlay();
    if (overlay) {
      overlay.classList.remove('hidden');
      overlay.removeAttribute('hidden');
      overlay.setAttribute('aria-hidden', 'false');
    }
    root()?.classList.add('is-busy');
    armCartBusySafetyTimer();
  }

  function hideMydCartBusy() {
    cartBusyDepth = Math.max(0, cartBusyDepth - 1);
    if (cartBusyDepth > 0) return;
    clearCartBusySafetyTimer();
    const overlay = busyOverlay();
    if (!overlay) return;
    overlay.classList.add('hidden');
    overlay.setAttribute('hidden', '');
    overlay.setAttribute('aria-hidden', 'true');
    root()?.classList.remove('is-busy');
  }

  function postCartJson(url, body) {
    return fetch(url, {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify(body),
    }).then(async (response) => {
      let parsed = null;
      try {
        parsed = await response.json();
      } catch (e) {
        parsed = null;
      }
      if (!response.ok) {
        const msg =
          (parsed && (parsed.description || parsed.message)) ||
          `Cart update failed (${response.status})`;
        throw new Error(msg);
      }
      if (parsed && parsed.status && parsed.message && !parsed.items && parsed.token == null) {
        throw new Error(parsed.description || parsed.message);
      }
      return parsed;
    });
  }

  function cartUpdate(updates) {
    const body = {
      updates,
      // Keep Liquid page_mode=true when re-rendering on the cart page
      sections_url: isMydCartPage()
        ? window.routes?.cart_url || '/cart'
        : window.location.pathname,
    };
    const sections = sectionsToRequest();
    if (sections.length) body.sections = sections;
    return postCartJson(window.routes.cart_update_url || '/cart/update.js', body);
  }

  function updateMydQuantity(row, input, quantity) {
    if (!row || !input || row.classList.contains('is-quantity-loading') || quantityUpdateLock) {
      return Promise.resolve();
    }

    const previousQuantity = Number(input.value) || 1;
    const nextQuantity = Math.max(0, Number(quantity) || 0);
    const itemKey = (row.dataset.key || '').trim();
    const line = Number(row.dataset.index);
    const variantId = (row.dataset.variantId || '').trim();
    if (!itemKey && !line && !variantId) return Promise.resolve();

    quantityUpdateLock = true;
    showMydCartBusy();
    row.classList.add('is-quantity-loading');
    if (nextQuantity > 0) input.value = String(nextQuantity);
    row.querySelectorAll('.myd-cart__qty button').forEach((button) => {
      button.disabled = true;
    });
    const errorText = row.querySelector('.cart-item__error-text');
    if (errorText) errorText.textContent = '';

    const changeUrl = window.routes.cart_change_url || '/cart/change.js';
    const updateUrl = window.routes.cart_update_url || '/cart/update.js';

    // Prefer line key (UpCart-proven). Never send sections on change — they 400 here.
    const attempts = [];
    if (itemKey) attempts.push(() => postCartJson(changeUrl, { id: itemKey, quantity: nextQuantity }));
    if (line > 0) attempts.push(() => postCartJson(changeUrl, { line, quantity: nextQuantity }));
    if (itemKey) {
      attempts.push(() => postCartJson(updateUrl, { updates: { [itemKey]: nextQuantity } }));
    }
    if (variantId) {
      attempts.push(() => postCartJson(updateUrl, { updates: { [variantId]: nextQuantity } }));
    }

    const runAttempts = (index, lastError) => {
      if (index >= attempts.length) {
        return Promise.reject(lastError || new Error('Could not update quantity'));
      }
      return attempts[index]().catch((err) => runAttempts(index + 1, err));
    };

    return runAttempts(0)
      .then((parsed) => {
        syncBillFromCart(parsed);
        const refresh =
          typeof window.MYDCartRefresh === 'function'
            ? window.MYDCartRefresh().catch(() => {
                if (nextQuantity <= 0 && row.isConnected) row.remove();
                document.dispatchEvent(
                  new CustomEvent('cart:updated', {
                    detail: { cart: parsed, source: 'myd-quantity' },
                  })
                );
                return parsed;
              })
            : Promise.resolve(parsed).then((cart) => {
                document.dispatchEvent(
                  new CustomEvent('cart:updated', {
                    detail: { cart, source: 'myd-quantity' },
                  })
                );
                return cart;
              });
        return refresh;
      })
      .catch((error) => {
        input.value = String(previousQuantity);
        if (errorText) errorText.textContent = error.message || 'Could not update quantity';
      })
      .finally(() => {
        quantityUpdateLock = false;
        hideMydCartBusy();
        const liveRow = itemKey
          ? document.querySelector(`.myd-cart__line[data-key="${CSS.escape(itemKey)}"]`) || row
          : row;
        liveRow.classList?.remove('is-quantity-loading');
        liveRow.querySelectorAll?.('.myd-cart__qty button').forEach((button) => {
          button.disabled = false;
        });
      });
  }

  function onQuantityButtonClick(event) {
    const button = event.target.closest('.myd-cart__qty .quantity__button');
    if (!button) return;
    event.preventDefault();
    event.stopImmediatePropagation();

    const currentRoot = root();
    if (!currentRoot?.contains(button)) return;

    const row = button.closest('.myd-cart__line');
    const input = row?.querySelector('.myd-cart__qty .quantity__input');
    if (!row || !input || button.disabled) return;

    const current = Number(input.value) || 1;
    if (button.name === 'minus' && current <= 1) {
      updateMydQuantity(row, input, 0);
      return;
    }
    const next = button.name === 'plus' ? current + 1 : Math.max(1, current - 1);
    if (next === current) return;
    updateMydQuantity(row, input, next);
  }

  function onQuantityInputCommit(event) {
    const input = event.target?.closest?.('.myd-cart__qty .quantity__input');
    if (!input) return;
    const currentRoot = root();
    if (!currentRoot?.contains(input)) return;

    const row = input.closest('.myd-cart__line');
    if (!row || row.classList.contains('is-quantity-loading')) return;

    const raw = String(input.value || '').trim();
    const next = Math.floor(Number(raw));
    if (!Number.isFinite(next) || next < 0) {
      input.value = input.dataset.mydQtyPrev || '1';
      return;
    }
    const prev = Number(input.dataset.mydQtyPrev);
    if (Number.isFinite(prev) && next === prev) return;
    updateMydQuantity(row, input, next);
  }

  function onQuantityInputFocus(event) {
    const input = event.target?.closest?.('.myd-cart__qty .quantity__input');
    if (!input || !root()?.contains(input)) return;
    input.dataset.mydQtyPrev = String(input.value || '1');
    // Allow typing over the selected value (QuantityInput also selects on focus)
    try {
      input.select();
    } catch (_) {}
  }

  function onQuantityInputKeydown(event) {
    const input = event.target?.closest?.('.myd-cart__qty .quantity__input');
    if (!input || !root()?.contains(input)) return;
    if (event.key === 'Enter') {
      event.preventDefault();
      input.blur();
    }
  }

  document.addEventListener('click', onQuantityButtonClick, true);
  document.addEventListener('focusin', onQuantityInputFocus, true);
  document.addEventListener('change', onQuantityInputCommit, true);
  document.addEventListener('keydown', onQuantityInputKeydown, true);

  function onCartQuickAddClick(event) {
    const button = event.target.closest('[data-myd-quick-add]');
    const currentRoot = root();
    if (!button || !currentRoot?.contains(button) || button.disabled) return;

    const quickAdd =
      window.__mydSearchQuickAddBound ||
      document.querySelector('myd-search-quick-add');
    const handle = button.getAttribute('data-product-handle');
    if (!quickAdd || typeof quickAdd.open !== 'function' || !handle) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    const rating =
      typeof quickAdd.readRatingFromTrigger === 'function'
        ? quickAdd.readRatingFromTrigger(button)
        : null;
    quickAdd.open(handle, {
      fromCart: true,
      forceTopSeller: button.getAttribute('data-myd-qa-topseller') === '1',
      rating,
    });
  }

  document.addEventListener('click', onCartQuickAddClick, true);

  function applySections(parsed) {
    restoreCouponDrawerToCart();
    const mini = document.querySelector('mini-cart');
    // updateContents = DOM only (no force-open); contentReplaced reasserts if was open
    if (mini && typeof mini.updateContents === 'function' && parsed.sections) {
      mini.updateContents(parsed);
      normalizeCartPageBill();
      bootMilestones(true);
      restoreSheetAfterRender();
      return;
    }
    if (!parsed.sections) return;
    const sectionName =
      document.getElementById('mini-cart')?.dataset?.section || 'mini-cart';
    const html = parsed.sections[sectionName] || parsed.sections['mini-cart'];
    if (!html) return;
    const drawer = document.querySelector('cart-drawer');
    const wasOpen = isCartDrawerOpen();
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const inner = doc.querySelector('.shopify-section');
    if (inner && document.getElementById('mini-cart')) {
      document.getElementById('mini-cart').innerHTML = inner.innerHTML;
    }
    normalizeCartPageBill();
    if (drawer && typeof drawer.contentReplaced === 'function') {
      drawer.contentReplaced({ wasOpen });
    }
    bootMilestones(true);
    restoreSheetAfterRender();
  }

  /**
   * Remove All (OOS): never use cartUpdate()+sections here.
   * Requesting sections on /cart/update.js often 400s (same class of bug as
   * single-line delete), which aborts the quantity change — UI may look busy
   * then unchanged, and checkout still sees the OOS lines.
   * Resolve live line keys from /cart.js, mutate without sections, then refresh.
   */
  function removeByVariantIds(variantIds) {
    const want = new Set((variantIds || []).map(String).filter(Boolean));
    if (!want.size) return Promise.resolve();

    const updateUrl = window.routes.cart_update_url || '/cart/update.js';
    showMydCartBusy();
    return fetch('/cart.js', {
      credentials: 'same-origin',
      cache: 'no-store',
      headers: { Accept: 'application/json' },
    })
      .then((response) => {
        if (!response.ok) throw new Error('Cart fetch failed');
        return response.json();
      })
      .then((cart) => {
        const updates = {};
        ((cart && cart.items) || []).forEach((item) => {
          if (want.has(String(item.variant_id))) updates[item.key] = 0;
        });
        if (!Object.keys(updates).length) return cart;
        return postCartJson(updateUrl, { updates });
      })
      .then((parsed) => {
        if (typeof window.MYDCartRefresh === 'function') {
          return window.MYDCartRefresh().catch(() => parsed);
        }
        applySections(parsed);
        document.dispatchEvent(
          new CustomEvent('cart:updated', { detail: { cart: parsed, source: 'myd-quantity' } })
        );
        return parsed;
      })
      .finally(() => {
        hideMydCartBusy();
      });
  }

  /**
   * Shopify regenerates every line-item key when a discount is applied
   * (/discount/CODE), so a key read off the already-rendered drawer goes stale
   * and /cart/change.js answers 400 — which used to make Delete a no-op once a
   * coupon was on the cart. Fall back the same way the quantity updater does,
   * including a live /cart.js lookup to recover the current key.
   */
  function removeByLineKey(key, line) {
    const changeUrl = window.routes.cart_change_url || '/cart/change.js';
    const updateUrl = window.routes.cart_update_url || '/cart/update.js';
    const qty = 0;
    const lineNum = Number(line);
    const hasLine = Number.isFinite(lineNum) && lineNum > 0;
    const variantId = key ? String(key).split(':')[0] : '';
    showMydCartBusy();

    const attempts = [];
    if (key) attempts.push(() => postCartJson(changeUrl, { id: key, quantity: qty }));
    if (hasLine) attempts.push(() => postCartJson(changeUrl, { line: lineNum, quantity: qty }));
    if (variantId) {
      // Re-resolve the key against the live cart: same position first (safest
      // when one variant sits on several lines), then by variant id.
      attempts.push(() =>
        fetch('/cart.js', { credentials: 'same-origin' })
          .then((response) => response.json())
          .then((cart) => {
            const items = (cart && cart.items) || [];
            const atIndex = hasLine ? items[lineNum - 1] : null;
            const match =
              (atIndex && String(atIndex.variant_id) === variantId && atIndex) ||
              items.find((item) => String(item.variant_id) === variantId);
            if (!match) throw new Error('Line is no longer in the cart');
            return postCartJson(changeUrl, { id: match.key, quantity: qty });
          })
      );
      attempts.push(() => postCartJson(updateUrl, { updates: { [variantId]: qty } }));
    }

    const runAttempts = (index, lastError) => {
      if (index >= attempts.length) {
        return Promise.reject(lastError || new Error('Could not remove item'));
      }
      return attempts[index]().catch((err) => runAttempts(index + 1, err));
    };

    return runAttempts(0)
      .then((parsed) => {
        if (typeof window.MYDCartRefresh === 'function') {
          return window.MYDCartRefresh().catch(() => parsed);
        }
        applySections(parsed);
        document.dispatchEvent(
          new CustomEvent('cart:updated', { detail: { cart: parsed, source: 'myd-quantity' } })
        );
        return parsed;
      })
      .finally(() => {
        hideMydCartBusy();
      });
  }

  function removeByLineIndex(index) {
    return removeByLineKey(null, index);
  }

  function moveToWishlist(item) {
    try {
      if (item?.handle && window.MydWishlist) {
        window.MydWishlist.addTo('default', item.handle);
      }
    } catch (e) {
      /* best-effort */
    }
    return removeByLineKey(item.key, item.index);
  }

  function onClick(event) {
    const removeAll = event.target.closest(SELECTOR.removeAll);
    if (removeAll) {
      event.preventDefault();
      const lines = root()?.querySelectorAll(SELECTOR.oosLines) || [];
      const variantIds = Array.from(lines)
        .map((el) => el.dataset.variantId)
        .filter(Boolean);
      if (!variantIds.length) return;
      removeAll.disabled = true;
      removeByVariantIds(variantIds).finally(() => {
        removeAll.disabled = false;
      });
      return;
    }

    const oosBtn = event.target.closest(SELECTOR.oosRemove);
    if (oosBtn) {
      event.preventDefault();
      event.stopPropagation();
      const line = oosBtn.closest('.myd-cart__line');
      openSheet({
        index: Number(oosBtn.dataset.index),
        key: oosBtn.dataset.key,
        variantId: line?.dataset?.variantId,
        productId: line?.dataset?.productId || '',
        handle: line?.dataset?.productHandle || '',
        title: line?.dataset?.productTitle || '',
        image: line?.dataset?.productImage || '',
        size: line?.dataset?.productSize || '',
        price: line?.dataset?.productPrice || '',
      });
      return;
    }

    if (event.target.closest(SELECTOR.sheetClose)) {
      event.preventDefault();
      event.stopPropagation();
      closeSheet();
      return;
    }

    if (event.target.closest(SELECTOR.sheetDelete)) {
      event.preventDefault();
      event.stopPropagation();
      if (typeof event.stopImmediatePropagation === 'function') {
        event.stopImmediatePropagation();
      }
      const target = resolvePending();
      if (!target?.key && !Number.isFinite(Number(target?.index))) return;
      const index = target.index;
      const key = target.key;
      closeSheet();
      removeByLineKey(key, index);
      return;
    }

    if (event.target.closest(SELECTOR.sheetWishlist)) {
      event.preventDefault();
      event.stopPropagation();
      if (typeof event.stopImmediatePropagation === 'function') {
        event.stopImmediatePropagation();
      }
      const target = resolvePending();
      if (!target?.key && !Number.isFinite(Number(target?.index))) return;
      const item = { ...target };
      closeSheet();
      moveToWishlist(item);
      return;
    }

    const removeBtn = event.target.closest('cart-remove-button');
    if (removeBtn && root()?.contains(removeBtn)) {
      event.preventDefault();
      const index = Number(removeBtn.dataset.index);
      const key = removeBtn.dataset.key || removeBtn.closest('.myd-cart__line')?.dataset?.key;
      if (!index && !key) return;
      removeByLineKey(key, index);
      return;
    }
  }

  document.addEventListener('click', onClick);

  /* —— Coupons panel —— */
  let appliedCouponCode = '';
  let lastCouponSavingsPaise = 0;
  let lastAppliedCouponMeta = null;
  let lastSyncedCart = null;
  let couponCatalogCache = null;
  let couponCatalogIdsCache = null;
  let couponSuccessAutoCloseTimer = null;
  let couponCatalogPromise = null;

  function couponPanel() {
    return document.querySelector('[data-myd-coupon-panel]');
  }

  function couponBackdrop() {
    return document.querySelector('.myd-cart__coupon-backdrop');
  }

  function mountCouponDrawerToViewport() {
    if (!isMydCartPage()) return;
    const panel = couponPanel();
    const backdrop = couponBackdrop();
    if (backdrop && backdrop.parentNode !== document.body) {
      document.body.appendChild(backdrop);
    }
    if (panel && panel.parentNode !== document.body) {
      document.body.appendChild(panel);
    }
  }

  function restoreCouponDrawerToCart() {
    const r = root();
    const panel = document.querySelector('body > [data-myd-coupon-panel]');
    const backdrop = document.querySelector('body > .myd-cart__coupon-backdrop');
    if (!r) {
      panel?.remove();
      backdrop?.remove();
      return;
    }
    if (backdrop) r.appendChild(backdrop);
    if (panel) r.appendChild(panel);
  }

  function openCouponPanel(opts) {
    const panel = couponPanel();
    const r = root();
    if (!panel || !r) return;
    const preserveMsg = !!(opts && opts.preserveMsg);
    const alreadyOpen = !panel.hidden && r.classList.contains('is-coupon-open');
    if (!preserveMsg) setCouponMsg('');
    // Avoid wiping an in-progress apply/remove error by re-painting on every reopen.
    if (!alreadyOpen) paintCoupons();
    mountCouponDrawerToViewport();
    panel.hidden = false;
    panel.removeAttribute('hidden');
    r.classList.add('is-coupon-open');
    const backdrop = couponBackdrop();
    if (backdrop) {
      backdrop.hidden = false;
      backdrop.removeAttribute('hidden');
    }
    try {
      r.querySelector('.myd-cart__shell')?.setAttribute('inert', '');
    } catch (_) {
      /* older WebKit without inert */
    }
  }

  function closeCouponPanel() {
    closeCouponAppliedSuccess();
    const panel = couponPanel();
    const r = root();
    if (panel) {
      panel.hidden = true;
      panel.setAttribute('hidden', '');
    }
    const backdrop = couponBackdrop();
    if (backdrop) {
      backdrop.hidden = true;
      backdrop.setAttribute('hidden', '');
    }
    restoreCouponDrawerToCart();
    if (r) r.classList.remove('is-coupon-open');
    const shell = r?.querySelector('.myd-cart__shell');
    if (shell) {
      shell.removeAttribute('inert');
      // Nudge iOS to repaint cart lines after the coupon overlay.
      shell.style.transform = 'translateZ(0)';
      requestAnimationFrame(() => {
        shell.style.transform = '';
      });
    }
    // Land back at the top of the drawer so items aren't left scrolled away
    // under the sticky tiers. Skip on /cart — the page should keep its scroll.
    if (!isMydCartPage()) {
      const body = getCartScrollEl();
      if (body) body.scrollTop = 0;
    }
  }

  function fetchCart() {
    return fetch(window.routes.cart_url ? `${window.routes.cart_url}.js` : '/cart.js', {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    }).then((r) => r.json()).then(normalizeCartDiscounts);
  }

  /**
   * /cart/update.js often nests apps as { amount, discount_application: { title, type } }
   * while /cart.js uses a flat { title, type, total_allocated_amount } shape.
   */
  function normalizeCartDiscounts(cart) {
    if (!cart || typeof cart !== 'object') return cart;
    const apps = cart.cart_level_discount_applications;
    if (!Array.isArray(apps) || !apps.length) return cart;
    if (!apps.some((a) => a && a.discount_application && !a.title)) return cart;
    return {
      ...cart,
      cart_level_discount_applications: apps.map((a) => {
        if (!a?.discount_application) return a;
        const inner = a.discount_application;
        return {
          ...inner,
          total_allocated_amount:
            a.total_allocated_amount ?? a.amount ?? inner.total_allocated_amount ?? 0,
        };
      }),
    };
  }

  function discountAppType(app) {
    return String(app?.type || '').trim().toLowerCase();
  }

  function isAutomaticDiscountApp(app) {
    const typ = discountAppType(app);
    if (typ === 'automatic') return true;
    if (typ === 'discount_code' || typ === 'script') return false;
    // /cart.js often returns type: "" for Admin automatic discounts.
    if (typ === '') {
      const t = String(app?.title || app?.code || '')
        .trim()
        .toUpperCase();
      if (!t || isNectorDiscountCode(t)) return false;
      return true;
    }
    return false;
  }

  /** True when a discount code (or Nector code) is actually removable — not automatic. */
  function isRemovableCodeApplied(cart, code) {
    const want = String(code || '')
      .trim()
      .toUpperCase();
    if (!want || !cart) return false;

    const codes = cart.discount_codes || [];
    const hit = codes.find((d) => String(d.code || '').toUpperCase() === want);
    if (hit && hit.applicable !== false) {
      if (Number(hit.amount) > 0 || hit.applicable === true) return true;
    }

    const apps = cart.cart_level_discount_applications || [];
    for (const a of apps) {
      if (isAutomaticDiscountApp(a)) continue;
      const t = String(a.title || a.code || '')
        .trim()
        .toUpperCase();
      if (t !== want) continue;
      const typ = discountAppType(a);
      if (typ === 'discount_code' || isNectorDiscountCode(t)) return true;
      // Empty/script types: only if Shopify also lists an applicable code entry
      if ((typ === '' || typ === 'script') && hit && hit.applicable !== false) return true;
    }
    return false;
  }

  function isCodeApplied(cart, code) {
    // Apply/remove success must not treat automatic discounts as a removable code.
    return isRemovableCodeApplied(cart, code);
  }

  function automaticDiscountSavingsPaise(cart) {
    if (!cart) return 0;
    const apps = cart.cart_level_discount_applications || [];
    return apps.reduce((sum, a) => {
      const t = String(a.title || a.code || '')
        .trim()
        .toUpperCase();
      if (!t) return sum;
      // Never count a real removable code as an "automatic" offer.
      if (isNectorDiscountCode(t) || discountAppType(a) === 'discount_code') return sum;
      if (isRemovableCodeApplied(cart, t)) return sum;
      if (!isAutomaticDiscountApp(a) && discountAppType(a) !== '') return sum;
      return sum + (Number(a.total_allocated_amount) || 0);
    }, 0);
  }

  function detectAutomaticOfferTitle(cart) {
    if (!cart) return '';
    const apps = cart.cart_level_discount_applications || [];
    for (const a of apps) {
      const t = String(a.title || '')
        .trim()
        .toUpperCase();
      if (!t || isNectorDiscountCode(t)) continue;
      if (discountAppType(a) === 'discount_code') continue;
      if (isRemovableCodeApplied(cart, t)) continue;
      if (!isAutomaticDiscountApp(a) && discountAppType(a) !== '') continue;
      if (Number(a.total_allocated_amount) > 0 || isAutomaticDiscountApp(a)) return t;
    }
    return '';
  }

  function parseMoneyToPaise(raw) {
    const n = Number(raw);
    if (!Number.isFinite(n) || n <= 0) return 0;
    // Ajax discount_codes.amount is usually a decimal currency string ("250.00")
    // while allocated amounts on applications are already in paise.
    if (typeof raw === 'string' && raw.includes('.')) return Math.round(n * 100);
    if (n >= 1000 && Number.isInteger(n)) return Math.round(n);
    if (n < 1000 && !Number.isInteger(n)) return Math.round(n * 100);
    // Ambiguous small integers: treat as rupees for discount_codes-style values
    return Math.round(n * 100);
  }

  function couponAmountPaise(cart, code) {
    const want = String(code || '').trim().toUpperCase();
    if (!want || !cart) return 0;

    const codes = cart.discount_codes || [];
    const hit = codes.find((d) => String(d.code || '').toUpperCase() === want);
    if (hit && hit.amount != null && hit.amount !== '') {
      const fromCode = parseMoneyToPaise(hit.amount);
      if (fromCode > 0) return fromCode;
    }

    const apps = cart.cart_level_discount_applications || [];
    const app = apps.find((a) => {
      const title = String(a.title || '').toUpperCase();
      const appCode = String(a.code || '').toUpperCase();
      return title === want || appCode === want;
    });
    if (app && Number(app.total_allocated_amount) > 0) {
      return Number(app.total_allocated_amount);
    }

    let sum = 0;
    for (const item of cart.items || []) {
      for (const d of item.discounts || []) {
        if (String(d.title || '').toUpperCase() === want) {
          sum += Number(d.amount || 0);
        }
      }
      for (const a of item.line_level_discount_allocations || []) {
        const t = String(
          a.discount_application?.title ||
            a.discount_application?.code ||
            ''
        ).toUpperCase();
        if (t === want) sum += Number(a.amount || 0);
      }
    }
    if (sum > 0) return sum;

    // Code is applied but amount fields were empty — cart.total_discount is coupon/script
    // discounts only (MRP compare-at is not included).
    const total = Number(cart.total_discount || 0);
    return total > 0 ? total : 0;
  }

  /** Cumulative MRP → selling savings (matches per-line "Saved ₹X"). */
  function productMrpSavingsFromDom() {
    const host = root();
    if (!host) return 0;
    return Array.from(host.querySelectorAll('.myd-cart__line[data-myd-saved-paise]')).reduce(
      (sum, el) => sum + (Number(el.dataset.mydSavedPaise) || 0),
      0
    );
  }

  function productMrpSavingsPaise(cart) {
    let fromCart = 0;
    if (cart && Array.isArray(cart.items)) {
      fromCart = cart.items.reduce((sum, item) => {
        const qty = Number(item.quantity) || 1;
        const sell = Number(item.final_price != null ? item.final_price : item.price) || 0;
        let mrp = Number(item.compare_at_price) || 0;
        // Match Liquid: fall back to original_price when compare-at is missing
        if (mrp <= sell) {
          mrp = Number(item.original_price) || 0;
        }
        if (mrp > sell) return sum + (mrp - sell) * qty;
        return sum;
      }, 0);
    }
    if (fromCart > 0) return fromCart;
    // /cart.js often omits compare_at — use values rendered by Liquid on each row
    return productMrpSavingsFromDom();
  }

  /**
   * Removable discount code only. Automatic store offers (e.g. FLAT300) must not
   * surface as an applied code with a Remove button — cart/update cannot clear them.
   */
  function detectAppliedCode(cart) {
    if (!cart) return '';
    const codes = cart.discount_codes || [];
    const hit = codes.find((d) => d && d.code && d.applicable !== false);
    if (hit) {
      const code = String(hit.code).trim().toUpperCase();
      if (Number(hit.amount) > 0 || hit.applicable === true || isNectorDiscountCode(code)) {
        return code;
      }
    }
    const apps = cart.cart_level_discount_applications || [];
    for (const a of apps) {
      if (isAutomaticDiscountApp(a)) continue;
      const t = String(a.title || a.code || '')
        .trim()
        .toUpperCase();
      if (!t) continue;
      const typ = discountAppType(a);
      if (typ === 'discount_code' || isNectorDiscountCode(t)) return t;
      if (typ === '' || typ === 'script') {
        const codeHit = codes.find((d) => String(d.code || '').toUpperCase() === t);
        if (codeHit && codeHit.applicable !== false) return t;
        if (isNectorDiscountCode(t)) return t;
      }
    }
    return '';
  }

  // Nector loyalty codes look like NECCBOX95CYTOLL… — not shop catalog codes.
  function isNectorDiscountCode(code) {
    const c = String(code || '')
      .trim()
      .toUpperCase();
    return c.length > 0 && /^NEC/i.test(c);
  }

  function uncheckNectorCheckbox() {
    try {
      const host = root()?.querySelector('[data-myd-nector-host]');
      const box = host?.querySelector('.myd-cart__nector-checkbox input[type="checkbox"]');
      // Avoid .click() — that can fire Nector's discount remove and clear HIGH5.
      if (box) box.checked = false;
      const applyBtn = host?.querySelector('[data-myd-nector-apply]');
      if (applyBtn && !nectorPendingRemove) {
        applyBtn.classList.remove('is-applied', 'is-removing');
        applyBtn.textContent = 'Apply';
      }
    } catch (_) {
      /* ignore */
    }
  }

  function cartItemTotalPaise(cart) {
    if (!cart) return 0;
    if (cart.items_subtotal_price != null) return Number(cart.items_subtotal_price) || 0;
    return (cart.items || []).reduce(
      (s, i) => s + Number(i.original_line_price || i.final_line_price || 0),
      0
    );
  }

  function syncBillFromCart(cart) {
    if (!cart) return;
    lastSyncedCart = cart;
    const detected = detectAppliedCode(cart);
    if (detected) appliedCouponCode = detected;
    else if (appliedCouponCode && !isCodeApplied(cart, appliedCouponCode)) {
      appliedCouponCode = '';
    }
    // Do NOT clear myCoins opt-in here when a shop coupon is still on the cart —
    // that cancelled mid-flight "Apply myCoins" while GET500 was waiting to be
    // replaced. Opt-out happens only in applyDiscountCode / remove / unapply.

    const want = appliedCouponCode;
    let couponPaise = want ? couponAmountPaise(cart, want) : 0;
    // Product Discount = MRP vs selling (not Shopify cart.total_discount)
    const productDiscount = productMrpSavingsPaise(cart);
    // If a code is applied but we still couldn't resolve amount, use total_discount
    if (want && couponPaise <= 0) {
      couponPaise = Number(cart.total_discount || 0) || 0;
    }
    // Automatic store offers still contribute to "You saved" even without Remove.
    if (!want && couponPaise <= 0) {
      couponPaise =
        automaticDiscountSavingsPaise(cart) || Number(cart.total_discount || 0) || 0;
    }

    const itemTotal = cartItemTotalPaise(cart);
    const grand = Number(cart.total_price || 0);

    lastCouponSavingsPaise = couponPaise;

    const cfgEl = document.getElementById('MydCartCouponData');
    if (cfgEl) {
      try {
        const cfg = JSON.parse(cfgEl.textContent || '{}');
        cfg.appliedCode = want;
        cfg.cartSubtotal = itemTotal;
        cfg.couponSaved = couponPaise;
        cfgEl.textContent = JSON.stringify(cfg);
      } catch (e) {
        /* ignore */
      }
    }

    updateBestCouponOfferRow(cart);

    if (want) {
      lastAppliedCouponMeta = extractAppliedMetaFromCart(cart, want);
      if (lastAppliedCouponMeta && couponPaise > 0) {
        lastAppliedCouponMeta.amountPaise = couponPaise;
      }
    } else {
      lastAppliedCouponMeta = null;
    }

    const setText = (sel, text) => {
      root()?.querySelectorAll(sel).forEach((el) => {
        el.textContent = text;
      });
    };

    setText('[data-myd-bill-items]', formatRupees(itemTotal));
    setText('[data-myd-bill-product-discount]', `-${formatRupeesTight(productDiscount)}`);
    const productRow = root()?.querySelector('[data-myd-bill-product-row]');
    if (productRow) productRow.hidden = productDiscount <= 0;

    const couponRow = root()?.querySelector('[data-myd-bill-coupon]');
    const couponVal = root()?.querySelector('[data-myd-bill-coupon-value]');
    const couponLab = root()?.querySelector('[data-myd-bill-coupon-label]');
    if (couponRow && couponVal) {
      if (want && couponPaise > 0) {
        couponRow.hidden = false;
        if (couponLab) couponLab.textContent = `Coupon (${want})`;
        couponVal.textContent = `-${formatRupeesTight(couponPaise)}`;
      } else if (want) {
        couponRow.hidden = false;
        if (couponLab) couponLab.textContent = `Coupon (${want})`;
        couponVal.textContent = 'Applied';
      } else if (couponPaise > 0) {
        couponRow.hidden = false;
        if (couponLab) couponLab.textContent = 'Coupon';
        couponVal.textContent = `-${formatRupeesTight(couponPaise)}`;
      } else {
        couponRow.hidden = true;
      }
    }

    const totalSavings = Math.max(0, productDiscount) + Math.max(0, couponPaise);
    const savedRow = root()?.querySelector('[data-myd-bill-summary-saved-row]');
    if (savedRow) savedRow.hidden = totalSavings <= 0;
    setText('[data-myd-bill-summary-saved]', formatRupeesTight(totalSavings));

    setText('[data-myd-bill-grand]', formatRupeesTight(grand));
    setText('[data-myd-bill-summary-total]', formatRupeesTight(grand));
    setText('[data-myd-footer-price]', formatRupeesTight(grand));

    root()?.querySelectorAll('[data-myd-footer-saved-wrap]')?.forEach((el) => {
      el.hidden = totalSavings <= 0;
    });
    setText('[data-myd-footer-saved]', formatRupeesTight(totalSavings).replace(/^₹/, ''));

    // Coupon panel sticky savings bar
    setText('[data-myd-coupon-foot-price]', formatRupeesTight(grand));
    setText('[data-myd-coupon-foot-saved]', formatRupeesTight(totalSavings));
    const footSaved = root()?.querySelector('[data-myd-coupon-foot-saved-wrap]');
    if (footSaved) footSaved.hidden = totalSavings <= 0;

    let mrpPaise = 0;
    if (cart && Array.isArray(cart.items)) {
      mrpPaise = cart.items.reduce((sum, item) => {
        const qty = Number(item.quantity) || 1;
        const sell = Number(item.final_price != null ? item.final_price : item.price) || 0;
        let mrp = Number(item.compare_at_price) || 0;
        if (mrp <= sell) mrp = Number(item.original_price) || 0;
        return sum + (mrp > sell ? mrp : sell) * qty;
      }, 0);
    }
    if (mrpPaise <= 0) mrpPaise = grand + totalSavings;
    const footMrpWrap = root()?.querySelector('[data-myd-coupon-foot-mrp-wrap]');
    const footMrp = root()?.querySelector('[data-myd-coupon-foot-mrp]');
    if (footMrpWrap && footMrp) {
      if (mrpPaise > grand) {
        footMrpWrap.hidden = false;
        footMrp.textContent = formatRupeesTight(mrpPaise);
      } else {
        footMrpWrap.hidden = true;
      }
    }
  }

  function syncCouponChrome(cart, code) {
    if (code) appliedCouponCode = String(code).trim().toUpperCase();
    syncBillFromCart(cart);
  }

  function fetchSectionsHtml(sectionIds) {
    const ids = [...new Set((sectionIds || []).filter(Boolean))];
    if (!ids.length) return Promise.resolve(null);
    // On /cart, fetch from the cart URL so Liquid page_mode stays true
    // (root /?sections=… renders the drawer <details> bill and collapses it).
    const rootPath = isMydCartPage()
      ? window.routes?.cart_url || '/cart'
      : window.Shopify?.routes?.root || '/';
    const separator = rootPath.includes('?') ? '&' : '?';
    const url = `${rootPath}${separator}sections=${ids.map(encodeURIComponent).join(',')}&_=${Date.now()}`;
    return fetch(url, {
      credentials: 'same-origin',
      cache: 'no-store',
      headers: { Accept: 'application/json' },
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((sections) => (sections ? { sections } : null))
      .catch(() => null);
  }

  function isMydCartPage() {
    return !!document.querySelector('[data-myd-cart-page]');
  }

  /** Keep Order Summary always expanded + static on the cart page after AJAX replaces. */
  function normalizeCartPageBill() {
    if (!isMydCartPage()) return;
    const cartRoot = root() || document.querySelector('[data-myd-cart]');
    if (!cartRoot) return;
    const bill = cartRoot.querySelector('[data-myd-bill]');
    if (!bill) return;

    bill.classList.add('myd-cart__bill--static');
    if (bill.tagName === 'DETAILS') {
      bill.setAttribute('open', '');
      // details[open] still keeps drawer chrome; page CSS hides collapsed rows.
    }

    const body = bill.querySelector('.myd-cart__bill-body');
    if (body) {
      body.hidden = false;
      body.removeAttribute('hidden');
      body.style.display = 'block';
      body.style.visibility = 'visible';
    }
  }

  function syncMydCartPageLayout() {
    const page = document.querySelector('[data-myd-cart-page]');
    if (!page) return;
    // Prefer live distance from viewport top → cart page (covers announcement + header).
    const top = Math.max(0, Math.ceil(page.getBoundingClientRect().top));
    const header =
      document.querySelector('.shopify-section-group-header-group') ||
      document.querySelector('.shopify-section-header');
    const headerH = header ? Math.ceil(header.getBoundingClientRect().height) : 0;
    const offset = Math.max(top, headerH, 56);
    document.documentElement.style.setProperty('--myd-cart-header-offset', `${offset}px`);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', syncMydCartPageLayout);
  } else {
    syncMydCartPageLayout();
  }
  window.addEventListener('resize', syncMydCartPageLayout, { passive: true });
  window.addEventListener('orientationchange', syncMydCartPageLayout, { passive: true });

  function isCartDrawerOpen() {
    if (isMydCartPage()) return true;
    return (
      document.body.classList.contains('mini-cart--open') ||
      document.body.classList.contains('mini-cart--opening') ||
      !!document.querySelector('cart-drawer details[open], details.mini-cart[open], details.cart-drawer-container[open]')
    );
  }

  document.addEventListener(
    'click',
    (event) => {
      const back = event.target.closest?.('[data-myd-cart-back]');
      if (!back) return;
      if (isMydCartPage()) return;
      event.preventDefault();
      back.dispatchEvent(
        new CustomEvent('drawer:force-close', {
          bubbles: true,
          cancelable: true,
          composed: true,
        })
      );
    },
    true
  );

  let keepOpenTimer = null;

  function clearKeepOpenTimer() {
    if (keepOpenTimer) {
      clearTimeout(keepOpenTimer);
      keepOpenTimer = null;
    }
  }

  window.MYDCartClearKeepOpen = clearKeepOpenTimer;

  function keepCartDrawerOpen() {
    const drawer = document.querySelector('cart-drawer');

    // Don't fight an intentional close (back button / cart icon / backdrop)
    if (drawer?._userClosed) return;
    if (drawer?.state === 'closed') return;

    if (drawer && typeof drawer.hold === 'function') {
      if (drawer.state === 'closing') drawer.state = 'open';
      drawer._closeGeneration = (drawer._closeGeneration || 0) + 1;
      drawer.hold('refresh', 550);
      if (typeof drawer.applyVisibility === 'function') drawer.applyVisibility();
    } else if (drawer && typeof drawer.holdOpen === 'function') {
      drawer.holdOpen(550);
    }

    // Re-assert after the 400ms close window in case a ghost close already started.
    clearKeepOpenTimer();
    keepOpenTimer = setTimeout(() => {
      keepOpenTimer = null;
      if (drawer?._userClosed) return;
      if (drawer?.state === 'closed') return;
      if (!isCartDrawerOpen()) return;
      if (drawer && typeof drawer.hold === 'function') {
        drawer.hold('refresh', 200);
        if (typeof drawer.applyVisibility === 'function') drawer.applyVisibility();
      } else if (drawer && typeof drawer.holdOpen === 'function') {
        drawer.holdOpen(200);
      }
    }, 450);
  }

  function setCartRefreshHold(on) {
    const drawer = document.querySelector('cart-drawer');
    if (on && drawer && typeof drawer.hold === 'function' && !drawer._userClosed) {
      drawer.hold('refresh', 800);
      return;
    }
    if (!on && drawer && typeof drawer.release === 'function') {
      drawer.release('refresh');
      return;
    }
    document.body.classList.toggle('myd-cart-refresh-hold', !!on);
  }

  function isMydCartPageDesktop() {
    return isMydCartPage() && window.matchMedia('(min-width: 990px)').matches;
  }

  function getCartScrollEl() {
    if (isMydCartPageDesktop()) {
      return document.scrollingElement || document.documentElement;
    }
    return root()?.querySelector('.myd-cart__body') || null;
  }

  function captureCartScroll() {
    const el = getCartScrollEl();
    return el ? el.scrollTop : 0;
  }

  function restoreCartScroll(y) {
    if (y == null || y < 0) return;
    const apply = () => {
      const el = getCartScrollEl();
      if (el) el.scrollTop = y;
    };
    apply();
    requestAnimationFrame(() => {
      apply();
      requestAnimationFrame(apply);
    });
    // Nector focus / late layout can still jump — re-assert shortly after
    window.setTimeout(apply, 50);
    window.setTimeout(apply, 200);
    window.setTimeout(apply, 500);
  }

  function refreshMydCartDrawer() {
    const mini = document.querySelector('mini-cart');
    const drawer = document.querySelector('cart-drawer');
    const sections = sectionsToRequest();
    const miniSection =
      mini?.dataset?.section ||
      document.getElementById('mini-cart')?.dataset?.section ||
      null;

    const sectionIds = sections.length
      ? sections
      : [miniSection, 'cart-icon-bubble'].filter(Boolean);

    const wasOpen = isCartDrawerOpen() && !drawer?._userClosed;
    const couponWasOpen = !!root()?.classList.contains('is-coupon-open');
    const savedScroll = wasOpen ? captureCartScroll() : 0;
    if (wasOpen) setCartRefreshHold(true);

    return Promise.all([
      fetch(window.routes.cart_url ? `${window.routes.cart_url}.js` : '/cart.js', {
        credentials: 'same-origin',
        cache: 'no-store',
        headers: { Accept: 'application/json' },
      }).then((r) => {
        if (!r.ok) throw new Error('Cart refresh failed');
        return r.json();
      }),
      fetchSectionsHtml(sectionIds),
    ])
      .then(([cart, sectionPayload]) => Object.assign({}, cart, sectionPayload || {}))
      .then((parsed) => {
        if (parsed?.sections) applySections(parsed);
        if (wasOpen && !drawer?._userClosed) keepCartDrawerOpen();
        // Section HTML resets the coupon panel — restore if it was open
        if (couponWasOpen && !drawer?._userClosed) openCouponPanel();
        else {
          root()?.classList.remove('is-coupon-open');
          root()?.querySelector('.myd-cart__shell')?.removeAttribute('inert');
          const panel = couponPanel();
          if (panel) {
            panel.hidden = true;
            panel.setAttribute('hidden', '');
          }
          const backdrop = couponBackdrop();
          if (backdrop) {
            backdrop.hidden = true;
            backdrop.setAttribute('hidden', '');
          }
        }
        syncBillFromCart(parsed);
        normalizeCartPageBill();
        if (wasOpen) restoreCartScroll(savedScroll);
        document.dispatchEvent(
          new CustomEvent('cart:updated', { detail: { cart: parsed, source: 'myd-refresh' } })
        );
        return parsed;
      })
      .finally(() => {
        if (!wasOpen) return;
        setTimeout(() => {
          restoreCartScroll(savedScroll);
          setCartRefreshHold(false);
        }, 500);
      });
  }

  window.MYDCartRefresh = refreshMydCartDrawer;

  let cartRefreshTimer = null;
  let cartRefreshPending = false;

  function cartPayloadHasSections(payload) {
    if (typeof payload === 'string') {
      try {
        payload = JSON.parse(payload);
      } catch (e) {
        return false;
      }
    }
    return !!(payload && payload.sections && Object.keys(payload.sections).length);
  }

  function scheduleMydCartRefresh(event) {
    if (quantityUpdateLock || cartRefreshPending || couponActionPending) return;
    const source = event?.detail?.source;
    if (
      source === 'myd-refresh' ||
      source === 'myd-quantity' ||
      source === 'myd-coupon' ||
      source === 'myd-nector' ||
      source === 'myd-nector-strip'
    ) {
      return;
    }
    const payload = event?.detail?.cart ?? event?.detail;
    if (cartPayloadHasSections(payload)) return;
    if (cartRefreshTimer) clearTimeout(cartRefreshTimer);
    cartRefreshTimer = setTimeout(() => {
      cartRefreshTimer = null;
      cartRefreshPending = true;
      refreshMydCartDrawer()
        .catch(() => {})
        .finally(() => {
          cartRefreshPending = false;
        });
    }, 40);
  }

  document.addEventListener('cart:refresh', scheduleMydCartRefresh);
  document.addEventListener('cart:updated', scheduleMydCartRefresh);
  document.addEventListener('product:added', scheduleMydCartRefresh);
  window.addEventListener('cart:refresh', scheduleMydCartRefresh);
  window.addEventListener('cart:updated', scheduleMydCartRefresh);

  function headerCartCount(payload) {
    if (payload == null) return null;
    const cart = payload.item_count != null ? payload : payload.cart || payload.detail || payload;
    const n = Number(cart?.item_count);
    return Number.isFinite(n) ? n : null;
  }

  function hideEmptyHeaderCartCount(event) {
    const count = headerCartCount(event?.detail) ?? headerCartCount(event?.detail?.cart);
    if (count == null) return;
    if (typeof window.MYD_SET_CART_COUNT === 'function') window.MYD_SET_CART_COUNT(count);
    document.querySelectorAll('.myd-header-cart .cart-count-bubble').forEach((bubble) => {
      const vis = bubble.querySelector('span[aria-hidden="true"]');
      if (count < 1) {
        bubble.hidden = true;
        bubble.classList.add('hidden', 'is-empty');
        if (vis) vis.textContent = '0';
      } else {
        bubble.hidden = false;
        bubble.classList.remove('hidden', 'is-empty', 'is-hidden');
        if (vis && count < 100) vis.textContent = String(count);
        else if (!vis && count < 100) {
          const n = document.createElement('span');
          n.setAttribute('aria-hidden', 'true');
          n.textContent = String(count);
          bubble.insertBefore(n, bubble.firstChild);
        }
      }
    });
  }

  document.addEventListener('cart:updated', hideEmptyHeaderCartCount);
  document.addEventListener('cart:refresh', hideEmptyHeaderCartCount);

  function parseMaybeJson(raw) {
    if (raw == null || raw === '') return null;
    if (typeof raw === 'object') return raw;
    if (typeof raw !== 'string') return null;
    try {
      return JSON.parse(raw);
    } catch (e) {
      return null;
    }
  }

  function loadCouponCatalog(cfg) {
    if (couponCatalogCache) return Promise.resolve(couponCatalogCache);
    if (couponCatalogPromise) return couponCatalogPromise;

    const fromSetting = parseMaybeJson(cfg.catalog);
    if (
      (Array.isArray(fromSetting) && fromSetting.length) ||
      (fromSetting && Array.isArray(fromSetting.coupons) && fromSetting.coupons.length)
    ) {
      couponCatalogCache = Array.isArray(fromSetting) ? fromSetting : fromSetting.coupons;
      couponCatalogIdsCache = Array.isArray(fromSetting?.ids)
        ? fromSetting.ids.map(String)
        : couponCatalogIdsCache;
      return Promise.resolve(couponCatalogCache);
    }

    const url = cfg.catalogUrl;
    if (!url) {
      couponCatalogCache = [];
      couponCatalogIdsCache = [];
      return Promise.resolve(couponCatalogCache);
    }

    couponCatalogPromise = fetch(url, { credentials: 'same-origin' })
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        let list = [];
        if (Array.isArray(data)) list = data;
        else if (data && Array.isArray(data.coupons)) list = data.coupons;
        couponCatalogCache = list;
        // Theme catalog is an allowlist source too (metaobject write may be unavailable).
        couponCatalogIdsCache = Array.isArray(data?.ids) ? data.ids.map(String) : [];
        return list;
      })
      .catch(() => {
        couponCatalogCache = [];
        couponCatalogIdsCache = [];
        return couponCatalogCache;
      });
    return couponCatalogPromise;
  }

  function normalizeCoupons(cfg, catalogOverride) {
    const meta = parseMaybeJson(cfg.meta) || {};
    const catalogRaw = catalogOverride || parseMaybeJson(cfg.catalog);
    let catalog = [];
    let catalogIds = Array.isArray(couponCatalogIdsCache) ? couponCatalogIdsCache.slice() : [];
    if (Array.isArray(catalogRaw)) catalog = catalogRaw;
    else if (catalogRaw && Array.isArray(catalogRaw.coupons)) {
      catalog = catalogRaw.coupons;
      if (Array.isArray(catalogRaw.ids)) catalogIds = catalogRaw.ids.map(String);
    }

    // Union metaobject ids + catalog ids so new codes (e.g. FLAT300) show even if
    // Content → Metaobjects app_discount json was not writable.
    const ids = [
      ...new Set([
        ...(Array.isArray(meta.ids) ? meta.ids.map(String) : []),
        ...catalogIds,
      ]),
    ];
    let coupons = [];
    if (Array.isArray(meta.coupons)) coupons = meta.coupons.slice();
    else if (Array.isArray(meta.items)) coupons = meta.items.slice();

    // Merge: catalog (fetched from Discount IDs) is the source of truth for details
    const byId = new Map();
    const byCode = new Map();
    const add = (c, prefer) => {
      if (!c) return;
      const id = c.id != null ? String(c.id) : '';
      const code = String(c.code || c.discount_code || '').trim().toUpperCase();
      if (id) {
        byId.set(id, prefer ? { ...(byId.get(id) || {}), ...c } : { ...c, ...(byId.get(id) || {}) });
      }
      if (code) {
        byCode.set(code, prefer ? { ...(byCode.get(code) || {}), ...c } : { ...c, ...(byCode.get(code) || {}) });
      }
    };
    coupons.forEach((c) => add(c, false));
    catalog.forEach((c) => add(c, true));

    let list = [];
    if (ids.length) {
      // Resolve each metaobject ID → coupon details from catalog
      ids.forEach((id) => {
        const hit = byId.get(String(id));
        if (hit) list.push(hit);
      });
    } else {
      const seen = new Set();
      [...byId.values(), ...byCode.values()].forEach((c) => {
        const key = String(c.id || c.code || '');
        if (!key || seen.has(key)) return;
        seen.add(key);
        list.push(c);
      });
    }

    // Deduplicate by code (metaobject ids often list the same discount twice)
    const seenCodes = new Set();
    list = list.filter((c) => {
      const code = String(c.code || c.discount_code || '').trim().toUpperCase();
      if (!code || seenCodes.has(code)) return false;
      seenCodes.add(code);
      return true;
    });

    return list
      .map((c) => {
        const explicitPaise = Number(c.min_order_paise ?? c.minPaise);
        const minOrder = Number(c.min_order ?? c.minOrder ?? c.minimum ?? c.threshold ?? 0);
        const minPaise =
          Number.isFinite(explicitPaise) && explicitPaise > 0
            ? explicitPaise
            : Number.isFinite(minOrder) && minOrder > 0
              ? Math.round(minOrder * 100)
              : 0;
        return {
          id: c.id != null ? String(c.id) : '',
          code: String(c.code || c.discount_code || '').trim().toUpperCase(),
          title: String(c.title || c.label || c.name || '').trim(),
          minPaise,
          expires: c.expires || c.expires_at || c.valid_till || c.validTill || '',
          value: Number(c.value ?? c.discount_value ?? 0),
          valueType: String(c.value_type || c.valueType || c.type || '').toLowerCase(),
          metaText: String(c.meta || c.description || c.details || c.summary || '').trim(),
        };
      })
      .filter((c) => c.code);
  }

  function couponTitle(c) {
    const title = String(c.title || '').trim();
    const code = String(c.code || '').trim();
    const titleIsCode = title && code && title.toUpperCase() === code.toUpperCase();
    if (c.value > 0 && (c.valueType.includes('percent') || c.valueType === 'percentage')) {
      return `${c.value}% Off`;
    }
    if (c.value > 0) return `Flat ₹${c.value} Off`;
    if (title && !titleIsCode) return title;
    return code;
  }

  function couponStubLabel(c) {
    if (c.value > 0 && (c.valueType.includes('percent') || c.valueType === 'percentage')) {
      return `${c.value}% OFF`;
    }
    if (c.value > 0) return `₹${c.value} OFF`;
    return 'OFFER';
  }

  function couponSaveLine(c, cartSubtotal, eligible) {
    if (eligible) {
      if (c.value > 0 && (c.valueType.includes('percent') || c.valueType === 'percentage')) {
        return `Save ${c.value}% on this order!`;
      }
      if (c.value > 0) return `Save ₹${c.value} on this order!`;
      return 'Coupon ready to apply on this order!';
    }
    const need = Math.max(0, (c.minPaise || 0) - cartSubtotal);
    const offer = couponTitle(c);
    if (need > 0) return `Add ${formatRupees(need)} more to get ${offer}`;
    return `Add more items to unlock ${offer}`;
  }

  /** Estimated savings in paise for ranking “Best for you”. Catalog fixed values are rupees. */
  function couponEstimatedSavingsPaise(c, cartSubtotal) {
    const sub = Number(cartSubtotal) || 0;
    const value = Number(c?.value) || 0;
    if (value <= 0) return 0;
    const type = String(c?.valueType || '').toLowerCase();
    if (type.includes('percent') || type === 'percentage') {
      return Math.max(0, Math.round((sub * value) / 100));
    }
    return Math.max(0, Math.round(value * 100));
  }

  function pickBestEligibleCoupon(coupons, cartSubtotal, applied, cart) {
    const want = String(applied || '').trim().toUpperCase();
    const autoTitles = new Set(
      (cart?.cart_level_discount_applications || [])
        .filter(isAutomaticDiscountApp)
        .map((a) =>
          String(a.title || '')
            .trim()
            .toUpperCase()
        )
        .filter(Boolean)
    );
    const available = (coupons || []).filter((c) => {
      if (!c?.code || isExpired(c.expires)) return false;
      if (want && c.code === want) return false;
      // Don't suggest Apply for a code that is already an automatic offer.
      if (autoTitles.has(String(c.code).toUpperCase())) return false;
      return !c.minPaise || cartSubtotal >= c.minPaise;
    });
    if (!available.length) return null;
    available.sort(
      (a, b) =>
        couponEstimatedSavingsPaise(b, cartSubtotal) -
          couponEstimatedSavingsPaise(a, cartSubtotal) ||
        String(a.code).localeCompare(String(b.code))
    );
    return available[0];
  }

  /**
   * Cart row: highlight best eligible offer + Apply, or applied / empty fallback.
   * “View all coupons” always opens the existing panel.
   */
  function updateBestCouponOfferRow(cart) {
    // paintCoupons() often calls this without a cart — reuse last sync so
    // automatic offers (FLAT300) are not wiped by the catalog "best" row.
    cart = cart || lastSyncedCart;
    const host = root()?.querySelector('[data-myd-coupons]');
    if (!host) return;

    const box = host.querySelector('[data-myd-coupons-box]');
    const row = host.querySelector('[data-myd-coupons-row]');
    const title = host.querySelector('[data-myd-coupons-title]');
    const label = host.querySelector('[data-myd-coupons-label]');
    const sub = host.querySelector('[data-myd-coupons-sub]');
    const applyBtn = host.querySelector('[data-myd-coupon-best-apply]');
    const removeBtn = host.querySelector('[data-myd-coupon-best-remove]');
    const savedBar = host.querySelector('[data-myd-coupons-saved]');
    const savedPrefix = host.querySelector('[data-myd-coupons-saved-prefix]');
    const savedAmt = host.querySelector('[data-myd-coupons-saved-amount]');
    const fallbackOpen = host.querySelector('[data-myd-coupons-fallback-open]');
    if (!row || !label) return;

    const cfgEl = document.getElementById('MydCartCouponData');
    let cfg = {};
    try {
      cfg = JSON.parse(cfgEl?.textContent || '{}');
    } catch (e) {
      cfg = {};
    }

    // Prefer live cart detection. Do not resurrect a stale Liquid/cfg code after Remove.
    const detected = cart ? detectAppliedCode(cart) : '';
    let want = String(detected || appliedCouponCode || '')
      .trim()
      .toUpperCase();
    if (!want && !cart) {
      want = String(cfg.appliedCode || '')
        .trim()
        .toUpperCase();
    }
    if (cart && want && !isRemovableCodeApplied(cart, want) && !isNectorDiscountCode(want)) {
      want = '';
      appliedCouponCode = '';
    } else if (want) {
      appliedCouponCode = want;
    } else {
      appliedCouponCode = '';
    }
    if (cfg && cart) cfg.appliedCode = appliedCouponCode;
    const removable = String(appliedCouponCode || '')
      .trim()
      .toUpperCase();
    const autoSavedPaise = automaticDiscountSavingsPaise(cart);
    const cartSubtotal =
      Number(cfg.cartSubtotal) ||
      cartItemTotalPaise(cart) ||
      0;

    /** mode: 'saved' | 'save' | null — no network; sync only */
    const setSavedBar = (paise, mode) => {
      const amount = Math.max(0, Number(paise) || 0);
      if (!savedBar) return;
      if (mode && amount > 0) {
        savedBar.hidden = false;
        if (savedPrefix) savedPrefix.textContent = mode === 'saved' ? 'You saved' : 'Save';
        if (savedAmt) savedAmt.textContent = formatRupeesTight(amount);
        if (box) {
          box.classList.toggle('is-applied', mode === 'saved');
          box.classList.toggle('is-offer', mode === 'save');
        }
      } else {
        savedBar.hidden = true;
        if (box) box.classList.remove('is-applied', 'is-offer');
      }
    };

    if (removable) {
      row.classList.add('is-applied');
      row.classList.remove('has-best', 'is-fallback');
      if (title) title.textContent = 'Offers & Rewards';
      label.textContent = `${removable} applied`;
      if (sub) sub.textContent = 'View all coupons';
      if (applyBtn) {
        applyBtn.hidden = true;
        applyBtn.removeAttribute('data-myd-coupon-apply');
        applyBtn.disabled = false;
      }
      if (removeBtn) {
        removeBtn.hidden = false;
        removeBtn.disabled = false;
        removeBtn.setAttribute('data-myd-coupon-remove', removable);
        removeBtn.setAttribute('aria-label', `Remove ${removable}`);
        if (!removeBtn.classList.contains('is-loading')) removeBtn.textContent = 'Remove';
      }
      if (fallbackOpen) fallbackOpen.hidden = true;

      const savedPaise =
        Math.max(0, Number(lastCouponSavingsPaise) || 0) ||
        (cart ? couponAmountPaise(cart, removable) || Number(cart.total_discount) || 0 : 0) ||
        Number(cfg.couponSaved) ||
        0;
      if (savedPaise > 0) {
        lastCouponSavingsPaise = savedPaise;
        setSavedBar(savedPaise, 'saved');
      }
      return;
    }

    // Automatic store offer (not a removable code) — show savings, never Remove.
    const autoTitle = detectAutomaticOfferTitle(cart);
    if (autoSavedPaise > 0 || autoTitle) {
      row.classList.add('is-applied');
      row.classList.remove('has-best', 'is-fallback');
      if (title) title.textContent = 'Offers & Rewards';
      label.textContent = autoTitle ? `${autoTitle} applied` : 'Offer applied';
      if (sub) sub.textContent = 'View all coupons';
      if (applyBtn) {
        applyBtn.hidden = true;
        applyBtn.removeAttribute('data-myd-coupon-apply');
      }
      if (removeBtn) {
        removeBtn.hidden = true;
        removeBtn.removeAttribute('data-myd-coupon-remove');
      }
      if (fallbackOpen) fallbackOpen.hidden = true;
      const saved =
        autoSavedPaise ||
        Number(cart?.total_discount || 0) ||
        Number(cfg.couponSaved || 0) ||
        0;
      if (saved > 0) setSavedBar(saved, 'saved');
      return;
    }

    const paintBest = (best) => {
      row.classList.remove('is-applied');
      if (removeBtn) {
        removeBtn.hidden = true;
        removeBtn.removeAttribute('data-myd-coupon-remove');
      }

      if (best?.code) {
        const savePaise = couponEstimatedSavingsPaise(best, cartSubtotal);
        row.classList.add('has-best');
        row.classList.remove('is-fallback');
        if (title) title.textContent = 'Offers & Rewards';
        label.textContent =
          savePaise > 0
            ? `Save ${formatRupeesTight(savePaise)} with "${best.code}"`
            : `Save with "${best.code}"`;
        if (sub) sub.textContent = 'View all coupons';
        if (applyBtn) {
          applyBtn.hidden = false;
          applyBtn.disabled = false;
          applyBtn.setAttribute('data-myd-coupon-apply', best.code);
          applyBtn.setAttribute('aria-label', `Apply ${best.code}`);
          if (!applyBtn.classList.contains('is-loading')) applyBtn.textContent = 'Apply';
        }
        if (fallbackOpen) fallbackOpen.hidden = true;
        setSavedBar(savePaise, savePaise > 0 ? 'save' : null);
        return;
      }

      // No eligible offer — keep a simple entry into the coupon panel
      row.classList.remove('has-best');
      row.classList.add('is-fallback');
      setSavedBar(0, null);
      if (title) title.textContent = 'Coupons';
      label.textContent = 'Apply Coupon';
      if (sub) sub.textContent = 'View available coupons';
      if (applyBtn) {
        applyBtn.hidden = true;
        applyBtn.removeAttribute('data-myd-coupon-apply');
      }
      if (fallbackOpen) fallbackOpen.hidden = false;
    };

    loadCouponCatalog(cfg)
      .then((catalog) => {
        const coupons = normalizeCoupons(cfg, catalog);
        paintBest(pickBestEligibleCoupon(coupons, cartSubtotal, removable, cart));
      })
      .catch(() => paintBest(null));
  }

  function couponDescLine(c) {
    if (c.metaText) return c.metaText;
    const offer = couponTitle(c);
    if (c.minPaise > 0) {
      return `Use code ${c.code} & get ${offer} on orders above ${formatRupees(c.minPaise)}`;
    }
    return `Use code ${c.code} & get ${offer}`;
  }

  function extractAppliedMetaFromCart(cart, code) {
    const want = String(code || '').trim().toUpperCase();
    if (!want || !cart) return null;

    const apps = cart.cart_level_discount_applications || [];
    let app = apps.find((a) => String(a.title || '').toUpperCase() === want);
    if (!app) {
      for (const item of cart.items || []) {
        const hit = (item.discounts || []).find(
          (d) => String(d.title || '').toUpperCase() === want
        );
        if (hit) {
          app = hit;
          break;
        }
      }
    }

    const codes = cart.discount_codes || [];
    const codeHit = codes.find((d) => String(d.code || '').toUpperCase() === want);

    let value = 0;
    let valueType = 'fixed';
    if (app) {
      const vt = String(app.value_type || '').toLowerCase();
      if (vt.includes('percent')) {
        valueType = 'percent';
        value = Number(app.value) || 0;
      } else if (app.value != null && app.value !== '') {
        valueType = 'fixed';
        value = Number(app.value) || 0;
      }
    } else if (codeHit) {
      const t = String(codeHit.type || '').toLowerCase();
      if (t.includes('percent')) valueType = 'percent';
    }

    const amountPaise = couponAmountPaise(cart, want);
    const parts = [];
    if (valueType.includes('percent') && value > 0) {
      parts.push(`${value}% off entire order`);
    } else if (amountPaise > 0) {
      parts.push(`${formatRupees(amountPaise)} off this order`);
    } else if (value > 0) {
      parts.push(`₹${value} off entire order`);
    }

    return {
      code: want,
      title: want,
      value,
      valueType,
      minPaise: 0,
      expires: '',
      metaText: parts.join(' • '),
      amountPaise,
    };
  }

  function formatExpiry(expires) {
    if (!expires) return '';
    const s = String(expires).trim();
    if (/[a-zA-Z]/.test(s) && !/^\d{4}-\d{2}-\d{2}/.test(s)) return s;
    const d = new Date(s);
    if (Number.isNaN(d.getTime())) return s;
    try {
      return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
    } catch (e) {
      return s;
    }
  }

  function isExpired(expires) {
    if (!expires) return false;
    const d = new Date(expires);
    if (Number.isNaN(d.getTime())) return false;
    const end = new Date(d);
    end.setHours(23, 59, 59, 999);
    return Date.now() > end.getTime();
  }

  function couponMetaLine(c, cartSubtotal, eligible) {
    const parts = [];
    if (c.minPaise > 0) {
      parts.push(`Valid on orders above ${formatRupees(c.minPaise)}`);
      if (!eligible) {
        const need = Math.max(0, c.minPaise - cartSubtotal);
        parts.push(`Add ${formatRupees(need)} more to unlock`);
      }
    }
    const exp = formatExpiry(c.expires);
    if (exp) parts.push(`Valid till ${exp}`);
    if (parts.length) return parts.join('. ') + '.';
    return c.metaText || '';
  }

  function setCouponMsg(text, ok) {
    const el = couponPanel()?.querySelector('[data-myd-coupon-msg]');
    if (!el) return;
    if (!text) {
      el.hidden = true;
      el.textContent = '';
      el.classList.remove('is-ok');
      return;
    }
    el.hidden = false;
    el.textContent = text;
    el.classList.toggle('is-ok', !!ok);
  }

  function couponSuccessModal() {
    return (
      couponPanel()?.querySelector('[data-myd-coupon-success]') ||
      root()?.querySelector('[data-myd-coupon-success]') ||
      null
    );
  }

  function closeCouponAppliedSuccess() {
    if (couponSuccessAutoCloseTimer) {
      clearTimeout(couponSuccessAutoCloseTimer);
      couponSuccessAutoCloseTimer = null;
    }
    const modal = couponSuccessModal();
    if (!modal) return;
    modal.hidden = true;
  }

  function loadConfetti() {
    if (window.MYDConfetti) return Promise.resolve(window.MYDConfetti);
    if (window.__mydConfettiPromise) return window.__mydConfettiPromise;

    const sources = [
      'https://cdn.jsdelivr.net/npm/canvas-confetti@1.9.3/dist/confetti.browser.min.js',
      'https://unpkg.com/canvas-confetti@1.9.3/dist/confetti.browser.min.js',
    ];

    window.__mydConfettiPromise = new Promise((resolve) => {
      const existing = document.querySelector('script[data-myd-confetti]');
      if (existing) {
        if (window.confetti) {
          window.MYDConfetti = window.confetti;
          resolve(window.MYDConfetti);
          return;
        }
        existing.addEventListener(
          'load',
          () => {
            window.MYDConfetti = window.confetti;
            resolve(window.MYDConfetti || null);
          },
          { once: true }
        );
        existing.addEventListener('error', () => resolve(null), { once: true });
        return;
      }

      const tryLoad = (index) => {
        if (index >= sources.length) {
          resolve(null);
          return;
        }
        const script = document.createElement('script');
        script.src = sources[index];
        script.async = true;
        script.dataset.mydConfetti = '1';
        script.onload = () => {
          window.MYDConfetti = window.confetti;
          resolve(window.MYDConfetti || null);
        };
        script.onerror = () => {
          script.remove();
          tryLoad(index + 1);
        };
        document.head.appendChild(script);
      };

      tryLoad(0);
    });

    return window.__mydConfettiPromise;
  }

  async function fireCouponConfetti(anchor) {
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return;
    const confetti = await loadConfetti();
    if (!confetti) return;

    const el = anchor?.isConnected
      ? anchor
      : couponSuccessModal()?.querySelector('.myd-cart__coupon-success-seal');
    const rect = el?.getBoundingClientRect?.();
    const origin = {
      x: rect ? (rect.left + rect.width / 2) / window.innerWidth : 0.5,
      y: rect ? (rect.top + rect.height / 2) / window.innerHeight : 0.42,
    };
    // Same palette as wishlist heart confetti
    const colors = ['#ffcc00', '#f43f5e', '#fb7185', '#fda4af', '#ffffff', '#e879f9'];
    const burst = (options) =>
      confetti({
        ...options,
        origin,
        colors,
        disableForReducedMotion: true,
        zIndex: 999999999,
      });

    burst({ particleCount: 120, spread: 72, startVelocity: 52, scalar: 1, ticks: 140 });
    burst({ particleCount: 80, spread: 115, startVelocity: 38, scalar: 1.1, ticks: 160 });
    burst({ particleCount: 60, spread: 360, startVelocity: 28, gravity: 0.6, scalar: 0.85, ticks: 120 });
    burst({ particleCount: 40, spread: 55, startVelocity: 58, scalar: 1.2, ticks: 100 });
  }

  function showCouponAppliedSuccess(code, savingsPaise) {
    const modal = couponSuccessModal();
    if (!modal) return;
    const cleaned = String(code || '').trim().toUpperCase();
    const amount = Math.max(0, Number(savingsPaise) || 0);
    const codeEl = modal.querySelector('[data-myd-coupon-success-code]');
    const titleEl = modal.querySelector('[data-myd-coupon-success-title]');
    const subEl = modal.querySelector('[data-myd-coupon-success-sub]');
    if (codeEl) codeEl.textContent = cleaned ? `‘${cleaned}’ applied` : 'Coupon applied';
    if (titleEl) {
      titleEl.textContent =
        amount > 0
          ? `${formatRupeesTight(amount)} savings with this coupon.`
          : 'Coupon applied successfully.';
    }
    if (subEl) {
      subEl.textContent = cleaned
        ? `Use ${cleaned} and save every time you shop`
        : 'Save more on your next order';
    }
    modal.hidden = false;
    const seal = modal.querySelector('.myd-cart__coupon-success-seal');
    // Next frame so the seal has layout for origin
    requestAnimationFrame(() => {
      fireCouponConfetti(seal);
    });
    if (couponSuccessAutoCloseTimer) clearTimeout(couponSuccessAutoCloseTimer);
    couponSuccessAutoCloseTimer = setTimeout(() => {
      couponSuccessAutoCloseTimer = null;
      closeCouponAppliedSuccess();
    }, 10000);
  }

  function paintCoupons() {
    const cfgEl = document.getElementById('MydCartCouponData');
    const panel = couponPanel();
    if (!cfgEl || !panel) return;

    let cfg;
    try {
      cfg = JSON.parse(cfgEl.textContent || '{}');
    } catch (e) {
      return;
    }

    const cartSubtotal = Number(cfg.cartSubtotal || 0);
    // appliedCode in cfg is Liquid-filtered to removable discount_code only.
    const applied = String(appliedCouponCode || cfg.appliedCode || '')
      .trim()
      .toUpperCase();
    if (applied) appliedCouponCode = applied;
    const deleteIconUrl = String(cfg.deleteIconUrl || '').trim();
    const appliedHost = panel.querySelector('[data-myd-coupon-applied]');
    const appliedList = panel.querySelector('[data-myd-coupon-applied-list]');
    const availHost = panel.querySelector('[data-myd-coupon-available]');
    const lockedHost = panel.querySelector('[data-myd-coupon-locked]');
    const availList = panel.querySelector('[data-myd-coupon-available-list]');
    const lockedList = panel.querySelector('[data-myd-coupon-locked-list]');
    const availTitle = panel.querySelector('[data-myd-coupon-available-title]');
    const input = panel.querySelector('[data-myd-coupon-input]');
    if (input && !input.dataset.touched) input.value = '';

    let empty = panel.querySelector('[data-myd-coupon-empty]');
    if (!empty) {
      empty = document.createElement('p');
      empty.className = 'myd-cart__coupon-empty';
      empty.setAttribute('data-myd-coupon-empty', '');
      panel.querySelector('[data-myd-coupon-lists]')?.prepend(empty);
    }

    const render = (catalog) => {
      const coupons = normalizeCoupons(cfg, catalog);
      const available = [];
      const locked = [];
      let appliedCoupon = null;

      coupons.forEach((c) => {
        if (isExpired(c.expires)) return;
        if (applied && c.code === applied) {
          appliedCoupon = c;
          return;
        }
        const eligible = !c.minPaise || cartSubtotal >= c.minPaise;
        if (eligible) available.push(c);
        else locked.push(c);
      });

      // Applied codes may not be in the metaobject ID list — still resolve from catalog/cart
      if (applied && !appliedCoupon) {
        const catalogList = Array.isArray(catalog)
          ? catalog
          : catalog && Array.isArray(catalog.coupons)
            ? catalog.coupons
            : [];
        const fromCatalog = catalogList.find(
          (c) => String(c.code || '').trim().toUpperCase() === applied
        );
        if (fromCatalog) {
          const mapped = normalizeCoupons(
            { meta: { ids: [], coupons: [fromCatalog] }, catalog: '' },
            [fromCatalog]
          )[0];
          if (mapped) appliedCoupon = mapped;
        }
      }

      if (applied && !appliedCoupon && lastAppliedCouponMeta) {
        appliedCoupon = { ...lastAppliedCouponMeta };
      }

      if (applied && !appliedCoupon) {
        appliedCoupon = {
          code: applied,
          title: applied,
          value: 0,
          valueType: 'fixed',
          minPaise: 0,
          expires: '',
          metaText: '',
        };
      }

      // Merge live cart meta (percent/amount) onto applied card
      if (appliedCoupon && lastAppliedCouponMeta && lastAppliedCouponMeta.code === applied) {
        if (lastAppliedCouponMeta.value > 0) {
          appliedCoupon.value = lastAppliedCouponMeta.value;
          appliedCoupon.valueType = lastAppliedCouponMeta.valueType;
        }
        if (lastAppliedCouponMeta.metaText && !appliedCoupon.metaText) {
          appliedCoupon.metaText = lastAppliedCouponMeta.metaText;
        }
      }

      if (availTitle) {
        availTitle.textContent = 'Best for you';
      }

      const deleteIconHtml = () => {
        if (deleteIconUrl) {
          return `<img class="myd-cart__coupon-card-delete-img" src="${escapeHtml(deleteIconUrl)}" alt="" width="16" height="18" decoding="async" />`;
        }
        return `<svg width="16" height="18" viewBox="0 0 24 26" fill="none" aria-hidden="true"><path d="M8 7V5h6v2" stroke="currentColor" stroke-width="2"/><path d="M5 8h12" stroke="currentColor" stroke-width="2" stroke-linecap="square"/><path d="M6 8v9a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1V8" stroke="currentColor" stroke-width="2"/><path d="M9 11v4M12 11v4" stroke="currentColor" stroke-width="2"/></svg>`;
      };

      const cardHtml = (c, eligible, forceApplied) => {
        const appliedHere = !!forceApplied || (applied && applied === c.code);
        const label = appliedHere ? 'APPLIED' : 'APPLY';
        const stub = couponStubLabel(c);
        let save = couponSaveLine(c, cartSubtotal, eligible);
        if (appliedHere && lastCouponSavingsPaise > 0) {
          save = `You saved ${formatRupees(lastCouponSavingsPaise)} on this order!`;
        } else if (appliedHere) {
          save = `Coupon ${c.code} is applied on this order!`;
        }
        const desc = couponDescLine(c);
        const deleteBtn = appliedHere
          ? `<button type="button" class="myd-cart__coupon-card-delete" data-myd-coupon-remove="${escapeHtml(c.code)}" aria-label="Remove ${escapeHtml(c.code)}">${deleteIconHtml()}</button>`
          : '';
        return `<article class="myd-cart__ticket${eligible || appliedHere ? '' : ' is-locked'}${appliedHere ? ' is-applied' : ''}${eligible && !appliedHere ? ' is-applyable' : ''}" data-coupon-code="${escapeHtml(c.code)}"${eligible && !appliedHere ? ` role="button" tabindex="0" aria-label="Apply ${escapeHtml(c.code)}"` : ''}>
          <div class="myd-cart__ticket-stub" aria-hidden="true">
            <span class="myd-cart__ticket-stub-text">${escapeHtml(stub)}</span>
          </div>
          <div class="myd-cart__ticket-body">
            <div class="myd-cart__ticket-top">
              <span class="myd-cart__ticket-code">${escapeHtml(c.code)}</span>
              <div class="myd-cart__ticket-actions">
                <button type="button" class="myd-cart__ticket-apply" data-myd-coupon-apply="${escapeHtml(c.code)}" ${eligible || appliedHere ? '' : 'disabled'} ${appliedHere ? 'disabled' : ''}>${label}</button>
              </div>
            </div>
            <p class="myd-cart__ticket-save">${escapeHtml(save)}</p>
            <div class="myd-cart__ticket-rule" aria-hidden="true"></div>
            <p class="myd-cart__ticket-desc">${escapeHtml(desc)}</p>
            ${deleteBtn ? `<div class="myd-cart__ticket-footer">${deleteBtn}</div>` : ''}
          </div>
        </article>`;
      };

      if (appliedList) {
        appliedList.innerHTML = appliedCoupon ? cardHtml(appliedCoupon, true, true) : '';
      }
      if (appliedHost) appliedHost.hidden = !appliedCoupon;

      if (availList) {
        if (available.length <= 1) {
          availList.innerHTML = available.map((c) => cardHtml(c, true, false)).join('');
        } else {
          available.sort(
            (a, b) =>
              couponEstimatedSavingsPaise(b, cartSubtotal) -
                couponEstimatedSavingsPaise(a, cartSubtotal) ||
              String(a.code).localeCompare(String(b.code))
          );
          const best = available[0];
          const more = available.slice(1);
          availList.innerHTML =
            cardHtml(best, true, false) +
            `<div class="myd-cart__coupon-group-title myd-cart__coupon-group-title--nested">More ways to save</div>` +
            more.map((c) => cardHtml(c, true, false)).join('');
        }
      }
      if (lockedList) lockedList.innerHTML = locked.map((c) => cardHtml(c, false, false)).join('');
      if (availHost) availHost.hidden = available.length === 0;
      if (lockedHost) lockedHost.hidden = locked.length === 0;

      if (!coupons.length && !appliedCoupon) {
        empty.hidden = false;
        empty.textContent = 'Enter a coupon code above to apply.';
      } else if (!available.length && !locked.length && !appliedCoupon) {
        empty.hidden = false;
        empty.textContent = 'No coupons available right now.';
      } else {
        empty.hidden = true;
        empty.textContent = '';
      }

      updateBestCouponOfferRow();
    };

    // Show loading while catalog (resolved from discount IDs) loads
    if (!couponCatalogCache) {
      empty.hidden = false;
      empty.textContent = 'Loading coupons…';
      if (appliedHost) appliedHost.hidden = true;
      if (availHost) availHost.hidden = true;
      if (lockedHost) lockedHost.hidden = true;
    }

    loadCouponCatalog(cfg).then(render);
  }

  function applyDiscountCode(code, control, opts) {
    const cleaned = String(code || '').trim().toUpperCase();
    const stayOnCart =
      !!(opts && opts.stayOnCart) ||
      !!(control && control.matches?.('[data-myd-coupon-best-apply]'));
    if (!cleaned) {
      setCouponMsg('Enter a coupon code');
      return Promise.resolve();
    }
    // Shopify carts only hold one discount code at a time. Applying a shop
    // coupon replaces myCoins — always suppress Nector auto-apply for non-
    // Nector codes so refresh / remount cannot silently re-pin NECC… over HIGH5.
    if (!beginCouponAction(control)) return Promise.resolve();
    setCouponMsg('');

    // Soft cart update only — section HTML swaps drop is-coupon-open and let the
    // backdrop / close-button eat a ghost click, which starts an uncancellable 400ms close.
    const postDiscount = (discountValue) => postCartDiscount(discountValue);

    const reopenPanel = (preserveMsg) => {
      if (stayOnCart) {
        keepCartDrawerOpen();
        return;
      }
      keepCartDrawerOpen();
      openCouponPanel({ preserveMsg: !!preserveMsg });
    };

    const applyFailureMsg = (cart, code) => {
      const autoTitle = detectAutomaticOfferTitle(cart);
      if (autoTitle) {
        return `${code} can't be combined with ${autoTitle}`;
      }
      return 'This coupon is not applicable on your cart';
    };

    return postDiscount(cleaned)
      .then(async (parsed) => {
        if (parsed.status && parsed.message && !parsed.token && !parsed.items) {
          reopenPanel(true);
          setCouponMsg(parsed.description || parsed.message || 'Could not apply this coupon');
          return;
        }

        let cart = parsed;
        if (!isCodeApplied(cart, cleaned)) {
          const alreadyRejected = (parsed.discount_codes || []).some(
            (d) =>
              String(d.code || '')
                .trim()
                .toUpperCase() === cleaned && d.applicable === false
          );
          // Automatic store offers (FLAT300) reject other codes as applicable:false.
          // Skip /discount fallback — it cannot beat automatic and can leave Apply stuck.
          if (!(alreadyRejected && detectAutomaticOfferTitle(parsed))) {
            // Cookie-based fallback used by older storefront discount flows.
            // Never follow redirects as a document navigation — that closes the drawer.
            await fetch(`/discount/${encodeURIComponent(cleaned)}`, {
              credentials: 'same-origin',
              redirect: 'manual',
            }).catch(() => null);
            cart = await fetchCart();
            if (!isCodeApplied(cart, cleaned)) {
              cart = await postDiscount(cleaned);
            }
          }
        }

        if (!isCodeApplied(cart, cleaned)) {
          syncCouponChrome(cart, '');
          paintCoupons();
          reopenPanel(true);
          setCouponMsg(applyFailureMsg(cart || parsed, cleaned));
          return;
        }

        appliedCouponCode = cleaned;
        if (!isNectorDiscountCode(cleaned)) {
          // Keep the new coupon's sticky cookie — do not clear after pin.
          setNectorWanted(false, { clearCookie: false });
          // Always pin this code in the sticky cookie so a leftover NECC…
          // cookie cannot win on the next reload.
          await fetch(`/discount/${encodeURIComponent(cleaned)}`, {
            credentials: 'same-origin',
            redirect: 'manual',
          }).catch(() => null);
          uncheckNectorCheckbox();
          nectorPendingForceApply = false;
          nectorMountedKey = '';
          // Drop the stale Nector SDK so the next myCoins Apply is a clean first tap.
          schedulePaintNector({ force: true });
        }
        syncCouponChrome(cart, cleaned);
        document.dispatchEvent(
          new CustomEvent('cart:updated', { detail: { cart, source: 'myd-coupon' } })
        );
        setCouponMsg('Coupon applied', true);
        const input = couponPanel()?.querySelector('[data-myd-coupon-input]');
        if (input) {
          input.value = '';
          delete input.dataset.touched;
        }
        paintCoupons();
        reopenPanel(true);
        if (!stayOnCart) {
          showCouponAppliedSuccess(cleaned, lastCouponSavingsPaise);
          // Re-assert after theme closeAnimation window in case a ghost close already started
          setTimeout(() => {
            keepCartDrawerOpen();
            openCouponPanel({ preserveMsg: true });
          }, 450);
        }
      })
      .catch(() => {
        reopenPanel(true);
        setCouponMsg('Could not apply this coupon');
      })
      .finally(() => {
        endCouponAction(control);
        reopenPanel(true);
      });
  }

  function removeDiscountCode(code, control, opts) {
    const cleaned = String(code || appliedCouponCode || '')
      .trim()
      .toUpperCase();
    const stayOnCart =
      !!(opts && opts.stayOnCart) ||
      !!(control && control.matches?.('[data-myd-coupon-best-remove]'));
    // Removing the live code (shop coupon or myCoins) — opt out of myCoins so
    // Nector cannot silently restore NECC… on the next open/reload.
    if (!beginCouponAction(control)) return Promise.resolve();
    setCouponMsg('');

    const reopenPanel = () => {
      if (stayOnCart) {
        keepCartDrawerOpen();
        return;
      }
      keepCartDrawerOpen();
      openCouponPanel();
    };

    return postCartDiscount('')
      .then(async (cart) => {
        if (cart.status && cart.message && !cart.items) {
          setCouponMsg(cart.description || cart.message || 'Could not remove coupon');
          return;
        }
        appliedCouponCode = '';
        lastCouponSavingsPaise = 0;
        lastAppliedCouponMeta = null;
        // Always opt out of myCoins when any coupon is removed — Nector's
        // backend coupon must not be allowed to /discount/NECC… on remount.
        setNectorWanted(false);
        uncheckNectorCheckbox();
        // cart/update.js alone does not clear Shopify's HttpOnly discount_code
        // cookie — purge via /discount/<noise> so refresh cannot resurrect NECC….
        await stripDiscountFully();
        let fresh = await fetchCart().catch(() => cart);
        if (isNectorDiscountCode(detectAppliedCode(fresh))) {
          fresh = (await stripDiscountFully()) || fresh;
          fresh = (await fetchCart().catch(() => fresh)) || fresh;
        }
        appliedCouponCode = '';
        syncCouponChrome(fresh || cart, '');
        if (isNectorDiscountCode(appliedCouponCode)) appliedCouponCode = '';
        const input = couponPanel()?.querySelector('[data-myd-coupon-input]');
        if (input) {
          input.value = '';
          delete input.dataset.touched;
        }
        document.dispatchEvent(
          new CustomEvent('cart:updated', { detail: { cart: fresh || cart, source: 'myd-coupon' } })
        );
        paintCoupons();
        schedulePaintNector({ force: true });
        reopenPanel();
        closeCouponAppliedSuccess();
        setCouponMsg(cleaned ? `${cleaned} removed` : 'Coupon removed', true);
        if (!stayOnCart) {
          setTimeout(() => {
            keepCartDrawerOpen();
            openCouponPanel();
          }, 450);
        }
      })
      .catch(() => setCouponMsg('Could not remove coupon'))
      .finally(() => {
        endCouponAction(control);
        reopenPanel();
      });
  }

  document.addEventListener('click', (event) => {
    if (event.target.closest('[data-myd-coupon-success-close]')) {
      event.preventDefault();
      closeCouponAppliedSuccess();
      return;
    }
    if (event.target.closest('[data-myd-coupons-open]')) {
      event.preventDefault();
      keepCartDrawerOpen();
      openCouponPanel();
      // Hold past an in-flight close animation / ghost click on first open (theme close is 400ms) —
      // same protection applyDiscountCode/removeDiscountCode already rely on via endCouponAction.
      setCouponDrawerLock(true);
      if (couponLockTimer) clearTimeout(couponLockTimer);
      couponLockTimer = setTimeout(() => {
        couponLockTimer = null;
        setCouponDrawerLock(false);
      }, 500);
      return;
    }
    if (event.target.closest('[data-myd-coupon-back]')) {
      event.preventDefault();
      closeCouponPanel();
      return;
    }
    const manual = event.target.closest('[data-myd-coupon-apply-manual]');
    if (manual) {
      event.preventDefault();
      const input = couponPanel()?.querySelector('[data-myd-coupon-input]');
      applyDiscountCode(input?.value, manual);
      return;
    }
    const removeBtn = event.target.closest('[data-myd-coupon-remove]');
    if (removeBtn && !removeBtn.disabled) {
      event.preventDefault();
      const fromCartRow = !!removeBtn.matches?.('[data-myd-coupon-best-remove]');
      if (fromCartRow) event.stopPropagation();
      removeDiscountCode(removeBtn.getAttribute('data-myd-coupon-remove'), removeBtn, {
        stayOnCart: fromCartRow,
      });
      return;
    }
    const ticket = event.target.closest('.myd-cart__ticket.is-applyable');
    if (ticket) {
      event.preventDefault();
      const code = ticket.getAttribute('data-coupon-code');
      const applyBtn = ticket.querySelector('[data-myd-coupon-apply]');
      if (code) applyDiscountCode(code, applyBtn || ticket);
      return;
    }
    const bestApply = event.target.closest('[data-myd-coupon-best-apply]');
    if (bestApply && !bestApply.disabled) {
      event.preventDefault();
      event.stopPropagation();
      applyDiscountCode(bestApply.getAttribute('data-myd-coupon-apply'), bestApply, {
        stayOnCart: true,
      });
      return;
    }
    const applyBtn = event.target.closest('[data-myd-coupon-apply]');
    if (applyBtn && !applyBtn.disabled) {
      event.preventDefault();
      applyDiscountCode(applyBtn.getAttribute('data-myd-coupon-apply'), applyBtn);
    }
  });

  document.addEventListener('input', (event) => {
    if (event.target.matches?.('[data-myd-coupon-input]')) {
      event.target.dataset.touched = '1';
    }
  });

  document.addEventListener('keydown', (event) => {
    if (event.target.matches?.('[data-myd-coupon-input]')) {
      if (event.key !== 'Enter') return;
      event.preventDefault();
      const manual = couponPanel()?.querySelector('[data-myd-coupon-apply-manual]');
      applyDiscountCode(event.target.value, manual);
      return;
    }
    if (event.key !== 'Enter' && event.key !== ' ') return;
    const ticket = event.target.closest?.('.myd-cart__ticket.is-applyable');
    if (ticket && event.target === ticket) {
      event.preventDefault();
      const code = ticket.getAttribute('data-coupon-code');
      const applyBtn = ticket.querySelector('[data-myd-coupon-apply]');
      if (code) applyDiscountCode(code, applyBtn || ticket);
    }
  });

  function bootCoupons() {
    // paintCoupons already updates the Offers row after catalog paint — no extra fetch.
    paintCoupons();
  }

  function schedulePaintNector(opts) {
    const options = opts || {};
    if (nectorScheduleTimer) clearTimeout(nectorScheduleTimer);
    nectorScheduleTimer = window.setTimeout(() => {
      nectorScheduleTimer = null;
      const run = () => {
        try {
          mountNectorCartWidget({
            ...options,
            forceApply: !!(options.forceApply || nectorPendingForceApply),
          });
        } catch (_) {}
      };
      if (typeof window.requestIdleCallback === 'function') {
        window.requestIdleCallback(run, { timeout: 800 });
      } else {
        run();
      }
    }, 120);
  }

  let nectorLoaderPromise = null;
  let nectorMountToken = 0;
  let nectorDiscountHooked = false;
  let nectorScheduleTimer = null;
  let nectorRefreshTimer = null;
  let nectorMountedKey = '';
  let nectorSuppressRemountUntil = 0;
  let nectorApplySyncTimer = null;
  // Survives remount races: static Apply → set wanted → schedulePaint must still redeem.
  let nectorPendingForceApply = false;
  let nectorPendingRemove = false;

  // myCoins is opt-in via mydNectorWanted:
  //   '1' = shopper tapped Apply — keep NECC across refresh
  //   '0' = shopper removed / swapped to a shop coupon — strip NECC on load
  // Never "adopt" a live NECC into '1' — that resurrected myCoins after remove.
  const NECTOR_WANTED_KEY = 'mydNectorWanted';
  const NECTOR_WANTED_COOKIE = 'myd_nector_wanted';
  const NECTOR_AUTO_APPLY_OFF_KEY = 'mydNectorAutoApplyOff'; // legacy

  function readNectorWantedCookie() {
    try {
      const m = document.cookie.match(/(?:^|;\s*)myd_nector_wanted=([01])/);
      return m ? m[1] : null;
    } catch (_) {
      return null;
    }
  }

  function writeNectorWantedCookie(wanted) {
    try {
      const v = wanted ? '1' : '0';
      const host = String(window.location.hostname || '');
      const base = `${NECTOR_WANTED_COOKIE}=${v}; path=/; Max-Age=31536000; SameSite=Lax`;
      document.cookie = base;
      if (host) {
        const parts = host.split('.');
        if (parts.length >= 2) {
          document.cookie = `${base}; domain=.${parts.slice(-2).join('.')}`;
        }
      }
    } catch (_) {
      /* ignore */
    }
  }

  function readNectorWantedFlag() {
    try {
      const ls = window.localStorage.getItem(NECTOR_WANTED_KEY);
      if (ls === '1' || ls === '0') return ls;
      if (window.localStorage.getItem(NECTOR_AUTO_APPLY_OFF_KEY) === '1') return '0';
    } catch (_) {
      /* ignore */
    }
    const ck = readNectorWantedCookie();
    if (ck === '1' || ck === '0') return ck;
    return null;
  }

  // Opted-in only when the shopper explicitly tapped Apply ('1').
  // A live NECC… on the cart alone is NOT enough — that let remove+refresh
  // resurrect myCoins when the sticky Shopify cookie re-applied the code.
  function getNectorWanted() {
    return readNectorWantedFlag() === '1';
  }

  // True only when the shopper explicitly turned myCoins off (remove / swap).
  function isNectorExplicitlyOff() {
    return readNectorWantedFlag() === '0';
  }

  function getNectorAutoApplySuppressed() {
    return isNectorExplicitlyOff();
  }

  // Nector applies its coin discount through Shopify's classic /discount/<code>
  // endpoint, which sets a sticky discount_code cookie. That cookie is often
  // HttpOnly — document.cookie cannot delete it — and Shopify re-applies
  // whatever code is in it on every page load. Nector itself clears by
  // hitting /discount/<random> (replaces the cookie with a non-code).
  function clearStickyDiscountCookie() {
    try {
      const expires = 'Thu, 01 Jan 1970 00:00:00 GMT';
      const host = String(window.location.hostname || '');
      const bases = ['discount_code=; Max-Age=0; path=/;', `discount_code=; expires=${expires}; path=/;`];
      bases.forEach((base) => {
        document.cookie = base;
        if (host) {
          document.cookie = `${base} domain=${host};`;
          const parts = host.split('.');
          if (parts.length >= 2) {
            document.cookie = `${base} domain=.${parts.slice(-2).join('.')};`;
          }
          if (parts.length >= 3) {
            document.cookie = `${base} domain=.${parts.slice(-3).join('.')};`;
          }
        }
      });
    } catch (_) {
      /* ignore */
    }
  }

  function purgeStickyDiscountViaShopify() {
    // Same clear trick Nector uses: /discount/<noise> overwrites HttpOnly cookie.
    const noise = `CLR${Date.now().toString(36)}${Math.random().toString(36).slice(2, 8)}`.toUpperCase();
    return fetch(`/discount/${encodeURIComponent(noise)}`, {
      credentials: 'same-origin',
      redirect: 'manual',
    }).catch(() => null);
  }

  function postCartDiscount(discountValue) {
    return fetch(window.routes.cart_update_url || '/cart/update.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({ discount: discountValue }),
    })
      .then((r) => r.json())
      .then(normalizeCartDiscounts);
  }

  // After an explicit coupon/myCoins removal, wipe cart discount + sticky cookie
  // so a refresh cannot resurrect NECC… via Shopify alone.
  function stripDiscountFully() {
    clearStickyDiscountCookie();
    return purgeStickyDiscountViaShopify()
      .then(() => postCartDiscount(''))
      .then(async (cart) => {
        // Sticky cookie / race can leave NECC… — one more purge if needed.
        const code = detectAppliedCode(cart) || '';
        if (isNectorDiscountCode(code)) {
          clearStickyDiscountCookie();
          await purgeStickyDiscountViaShopify();
          return postCartDiscount('');
        }
        return cart;
      })
      .catch(() => null);
  }

  function setNectorWanted(wanted, opts) {
    try {
      window.localStorage.setItem(NECTOR_WANTED_KEY, wanted ? '1' : '0');
      if (wanted) {
        window.localStorage.removeItem(NECTOR_AUTO_APPLY_OFF_KEY);
      } else {
        window.localStorage.setItem(NECTOR_AUTO_APPLY_OFF_KEY, '1');
        if (!opts || opts.clearCookie !== false) {
          clearStickyDiscountCookie();
        }
      }
    } catch (_) {
      /* ignore */
    }
    writeNectorWantedCookie(wanted);
  }

  // Back-compat alias used by coupon apply/remove paths.
  function setNectorAutoApplySuppressed(suppressed, opts) {
    setNectorWanted(!suppressed, opts);
  }

  // Strip resurrected NECC… only when the shopper explicitly opted out ('0').
  // When opted in ('1'), leave the sticky/Shopify code alone so refresh keeps it.
  // Never write '1' just because NECC… is on the cart (that undid intentional remove).
  let nectorStripInFlight = null;
  function enforceSuppressedNectorGone() {
    if (nectorPendingForceApply) return Promise.resolve();
    if (nectorStripInFlight) return nectorStripInFlight;

    if (!isNectorExplicitlyOff()) {
      // Opted in or no choice yet — sync chrome if NECC… is live, but do not
      // flip the flag to '1' (remove must stay sticky across refresh).
      nectorStripInFlight = fetchCart()
        .then((cart) => {
          const code = detectAppliedCode(cart) || '';
          if (isNectorDiscountCode(code) && getNectorWanted()) {
            syncBillFromCart(cart);
            paintCoupons();
          } else if (isNectorDiscountCode(code) && readNectorWantedFlag() == null) {
            // Legacy session with no flag: keep what Shopify already applied,
            // without locking opt-in so a later remove can still stick.
            syncBillFromCart(cart);
            paintCoupons();
          }
        })
        .catch(() => null)
        .finally(() => {
          nectorStripInFlight = null;
        });
      return nectorStripInFlight;
    }

    nectorStripInFlight = fetchCart()
      .then(async (cart) => {
        if (!isNectorExplicitlyOff() || nectorPendingForceApply) return;
        const code = detectAppliedCode(cart) || appliedCouponCode;
        if (!isNectorDiscountCode(code) && !isNectorDiscountCode(appliedCouponCode)) {
          clearStickyDiscountCookie();
          return;
        }
        let fresh = await stripDiscountFully();
        // Retry — Shopify can re-read the old cookie mid-flight.
        for (let i = 0; i < 2; i++) {
          if (!isNectorExplicitlyOff() || nectorPendingForceApply) return;
          const check = await fetchCart().catch(() => null);
          const still = detectAppliedCode(check) || '';
          if (!isNectorDiscountCode(still)) {
            fresh = check || fresh;
            break;
          }
          fresh = await stripDiscountFully();
        }
        if (!isNectorExplicitlyOff() || nectorPendingForceApply) return;
        appliedCouponCode = '';
        lastCouponSavingsPaise = 0;
        lastAppliedCouponMeta = null;
        if (!fresh || !fresh.items) {
          fresh = await fetchCart().catch(() => null);
        }
        if (fresh && fresh.items) {
          // Force-clear detected NEC code even if cart.js briefly lags.
          if (isNectorDiscountCode(detectAppliedCode(fresh))) {
            appliedCouponCode = '';
          } else {
            syncBillFromCart(fresh);
          }
          paintCoupons();
          document.dispatchEvent(
            new CustomEvent('cart:updated', { detail: { cart: fresh, source: 'myd-nector-strip' } })
          );
        }
      })
      .catch(() => null)
      .finally(() => {
        nectorStripInFlight = null;
      });
    return nectorStripInFlight;
  }

  function nectorShellHtml(applicable, applied) {
    return (
      '<div class="myd-cart__nector" data-myd-nector>' +
      '<div class="myd-cart__nector-title">myCoins</div>' +
      '<div class="myd-cart__nector-card" data-myd-nector-card>' +
      '<span class="myd-cart__nector-left">' +
      '<span class="myd-cart__nector-icon" aria-hidden="true">' +
      '<svg width="17" height="17" viewBox="0 0 24 24" fill="none">' +
      '<circle cx="12" cy="12" r="10" fill="currentColor"/>' +
      '<circle cx="12" cy="12" r="10" stroke="#181818" stroke-opacity="0.15"/>' +
      '<circle cx="12" cy="12" r="7.5" stroke="#181818" stroke-opacity="0.25"/>' +
      '<text x="12" y="16.3" font-size="11" font-weight="800" text-anchor="middle" fill="#181818">₹</text>' +
      '</svg>' +
      '</span>' +
      '<span class="myd-cart__nector-copy">' +
      `<strong class="myd-cart__nector-amount">₹${applicable}</strong>` +
      `<em class="myd-cart__nector-sub">Available — save up to ₹${applicable} using myCoins</em>` +
      '</span>' +
      '</span>' +
      `<button type="button" class="myd-cart__nector-apply${applied ? ' is-applied' : ''}" data-myd-nector-apply>${
        applied ? 'Remove' : 'Apply'
      }</button>` +
      '<div class="myd-cart__nector-mount" data-myd-nector-mount></div>' +
      '</div>' +
      '</div>'
    );
  }

  function nectorPriceFromConfig(cfg) {
    // Prefer raw paise → rupees (avoids "8,172.00" locale money strings).
    const paise = Number(cfg && cfg.cartTotal);
    if (Number.isFinite(paise) && paise > 0) {
      return String(Math.round(paise / 100));
    }
    const raw = String((cfg && cfg.price) || '').replace(/[^0-9.]/g, '');
    const n = Number(raw);
    return Number.isFinite(n) && n > 0 ? String(n) : '';
  }

  // Install immediately so Nector cannot race /discount/NECC before first mount.
  function hookNectorDiscountRefresh() {
    if (nectorDiscountHooked) return;
    nectorDiscountHooked = true;
    const origFetch = window.fetch;
    if (typeof origFetch === 'function') {
      window.fetch = function patchedFetch(input, init) {
        const url = typeof input === 'string' ? input : input && input.url;
        const urlStr = String(url || '');
        if (urlStr.indexOf('/discount/') !== -1) {
          const raw = urlStr.split('/discount/')[1] || '';
          const code = decodeURIComponent(raw.split(/[?#]/)[0] || '')
            .trim()
            .toUpperCase();
          // Opt-in gate: only allow /discount/NECC… when shopper tapped Apply.
          // Block when opted out OR when flag is missing (PDP widget / remount).
          if (isNectorDiscountCode(code) && !getNectorWanted()) {
            return Promise.resolve(new Response('', { status: 204 }));
          }
        }
        const p = origFetch.apply(this, arguments);
        if (urlStr.indexOf('/discount/') !== -1) {
          const raw = urlStr.split('/discount/')[1] || '';
          const code = decodeURIComponent(raw.split(/[?#]/)[0] || '')
            .trim()
            .toUpperCase();
          // Only soft-refresh for real Nector applies, not CLR noise purges.
          if (isNectorDiscountCode(code) && getNectorWanted()) {
            p.then(refreshCartAfterNectorDiscount).catch(() => {});
          }
        }
        return p;
      };
    }
    document.addEventListener(
      'change',
      (event) => {
        if (!event.target?.closest?.('[data-myd-nector-host]')) return;
        if (!getNectorWanted()) return;
        refreshCartAfterNectorDiscount();
      },
      true
    );
  }
  hookNectorDiscountRefresh();

  function loadNectorSdk(scriptUrl) {
    if (window.nector_sdk && typeof window.nector_sdk.init_widget === 'function') {
      return Promise.resolve(window.nector_sdk);
    }
    if (nectorLoaderPromise) return nectorLoaderPromise;
    nectorLoaderPromise = new Promise((resolve, reject) => {
      const existing = document.querySelector('script[data-myd-nector-loader]');
      if (existing) {
        const wait = () => {
          if (window.nector_sdk && typeof window.nector_sdk.init_widget === 'function') {
            resolve(window.nector_sdk);
          } else {
            window.setTimeout(wait, 50);
          }
        };
        wait();
        return;
      }
      const script = document.createElement('script');
      script.src = scriptUrl;
      script.async = true;
      script.dataset.mydNectorLoader = '1';
      script.onload = () => {
        if (window.nector_sdk && typeof window.nector_sdk.init_widget === 'function') {
          resolve(window.nector_sdk);
        } else {
          let tries = 0;
          const tick = () => {
            if (window.nector_sdk && typeof window.nector_sdk.init_widget === 'function') {
              resolve(window.nector_sdk);
            } else if (tries++ < 40) {
              window.setTimeout(tick, 50);
            } else {
              reject(new Error('Nector SDK missing'));
            }
          };
          tick();
        }
      };
      script.onerror = () => reject(new Error('Nector loader failed'));
      document.body.appendChild(script);
    }).catch((err) => {
      nectorLoaderPromise = null;
      throw err;
    });
    return nectorLoaderPromise;
  }

  function refreshCartAfterNectorDiscount() {
    // Soft update only — full section HTML replace jumps scroll to top.
    nectorSuppressRemountUntil = Date.now() + 5000;
    if (nectorRefreshTimer) clearTimeout(nectorRefreshTimer);
    nectorRefreshTimer = window.setTimeout(() => {
      nectorRefreshTimer = null;
      const savedScroll = captureCartScroll();
      fetchCart()
        .then((cart) => {
          syncBillFromCart(cart);
          paintCoupons();
          document.dispatchEvent(
            new CustomEvent('cart:updated', { detail: { cart, source: 'myd-nector' } })
          );
          restoreCartScroll(savedScroll);
        })
        .catch(() => {
          restoreCartScroll(savedScroll);
        });
    }, 500);
  }

  function readNectorConfig() {
    const dataEl = document.getElementById('MydCartNectorData');
    if (!dataEl) return null;
    try {
      return JSON.parse(dataEl.textContent || '{}');
    } catch (_) {
      return null;
    }
  }

  function mountNectorCartWidget(opts) {
    const options = opts || {};
    const host = root()?.querySelector('[data-myd-nector-host]');
    if (!host) return;

    const cfg = readNectorConfig();
    const balance = Math.max(0, Number(cfg && cfg.balance) || 0);
    const customerId = cfg && cfg.customerId;
    const apiKey = String((cfg && cfg.apiKey) || '').trim();
    const scriptUrl = String((cfg && cfg.scriptUrl) || '').trim();
    const platformUrl = String((cfg && cfg.platformUrl) || 'https://platform.nector.io').trim();
    const price = nectorPriceFromConfig(cfg);
    const applicable = Math.max(0, Number(cfg && cfg.applicable) || 0);
    const containerId = 'myd-cart-nector-useyourcoins';
    const mountKey = `${customerId || ''}|${apiKey}|${balance}|${price}`;
    const shopCouponLive =
      !!appliedCouponCode && !isNectorDiscountCode(appliedCouponCode);
    const forceApply = !!(options.forceApply || nectorPendingForceApply);

    if (nectorApplySyncTimer) {
      clearInterval(nectorApplySyncTimer);
      nectorApplySyncTimer = null;
    }

    if (!customerId || !apiKey || balance <= 0 || !scriptUrl) {
      host.innerHTML = '';
      host.hidden = true;
      nectorMountedKey = '';
      nectorPendingForceApply = false;
      return;
    }

    if (!price) {
      // Can't redeem without a cart price — keep Apply visible for retry.
      host.hidden = false;
      host.dataset.mydNectorReady = '0';
      nectorMountedKey = '';
      nectorPendingForceApply = false;
      host.innerHTML = nectorShellHtml(applicable, false);
      const applyBtn = host.querySelector('[data-myd-nector-apply]');
      if (applyBtn) {
        applyBtn.disabled = false;
        applyBtn.textContent = 'Apply';
        applyBtn.onclick = (event) => {
          event.preventDefault();
          event.stopPropagation();
          beginNectorApplyFromButton(applyBtn);
        };
      }
      return;
    }

    // Catalog coupon wins — never keep myCoins wanted.
    if (shopCouponLive && getNectorWanted() && !forceApply) {
      setNectorWanted(false, { clearCookie: false });
    }

    const effectiveWanted = getNectorWanted() || forceApply;

    // Static shell only when myCoins is not opted-in. Mounting the SDK would
    // re-apply a leftover Nector backend coupon via /discount/NECC… on init.
    if (!effectiveWanted) {
      host.hidden = false;
      host.dataset.mydNectorReady = '0';
      nectorMountedKey = '';
      host.innerHTML = nectorShellHtml(applicable, false);
      const applyBtn = host.querySelector('[data-myd-nector-apply]');
      if (applyBtn) {
        applyBtn.disabled = false;
        applyBtn.textContent = 'Apply';
        applyBtn.onclick = (event) => {
          event.preventDefault();
          event.stopPropagation();
          beginNectorApplyFromButton(applyBtn);
        };
      }
      return;
    }

    // Already applied + opted in: show Applied without mounting the SDK.
    // Remounting with auto_apply re-hits /discount/NECC… and fights remove+refresh.
    if (!forceApply && isNectorDiscountCode(appliedCouponCode)) {
      host.hidden = false;
      host.dataset.mydNectorReady = '0';
      nectorMountedKey = '';
      host.innerHTML = nectorShellHtml(applicable, true);
      wireNectorApplyButton(host);
      return;
    }

    const existing = host.querySelector(`#${containerId}`);
    if (!options.force && !forceApply && existing && existing.childNodes.length && nectorMountedKey === mountKey && host.dataset.mydNectorReady === '1') {
      wireNectorApplyButton(host);
      return;
    }
    if (!options.force && !forceApply && Date.now() < nectorSuppressRemountUntil && host.dataset.mydNectorReady === '1' && existing) {
      wireNectorApplyButton(host);
      return;
    }

    const token = ++nectorMountToken;
    // When the shopper opted in (or just tapped Apply), allow Nector's init
    // redemption. auto_apply false left Apply doing nothing after the static-shell change.
    const autoApply = 'true';
    const savedScroll = captureCartScroll();
    host.hidden = false;
    host.dataset.mydNectorReady = '0';
    host.innerHTML = nectorShellHtml(
      applicable,
      forceApply || isNectorDiscountCode(appliedCouponCode)
    );
    const applyingBtn = host.querySelector('[data-myd-nector-apply]');
    if (applyingBtn && forceApply) {
      applyingBtn.disabled = true;
      applyingBtn.textContent = 'Applying…';
    }

    const mount = host.querySelector('[data-myd-nector-mount]');
    if (!mount) return;

    hookNectorDiscountRefresh();
    restoreCartScroll(savedScroll);

    const stopFocusJump = (event) => {
      if (!host.contains(event.target)) return;
      restoreCartScroll(savedScroll);
    };
    host.addEventListener('focusin', stopFocusJump, true);

    loadNectorSdk(scriptUrl)
      .then((sdk) => {
        if (token !== nectorMountToken || !mount.isConnected) return;
        // Nector's loader creates `#target_element_id` inside this container.
        // Do NOT pre-create that id here — duplicate ids make the widget bind
        // to an empty node and Apply hangs on "Applying…".
        mount.innerHTML = '';
        sdk.init_widget(
          'useyourcoinscheckbox',
          {
            api_key: apiKey,
            customer_id: String(customerId),
            platform: 'shopify',
            platform_url: platformUrl,
            price: price,
            product_id: '',
            page_type: 'cart',
            auto_apply: autoApply,
            hide_if_not_loggedin: 'true',
            hide_border: 'true',
            useyourcoins_checkbox_classes: 'myd-cart__nector-checkbox',
            target_element_id: containerId,
            containerId: containerId,
            force_refresh: forceApply ? 'true' : 'false',
          },
          mount
        );
        nectorMountedKey = mountKey;
        host.dataset.mydNectorReady = '1';
        wireNectorApplyButton(host);
        if (forceApply) {
          watchNectorApplyResult({ remountOnce: false, token });
        } else {
          nectorPendingForceApply = false;
        }
        restoreCartScroll(savedScroll);
        window.setTimeout(() => restoreCartScroll(savedScroll), 300);
      })
      .catch(() => {
        if (token !== nectorMountToken) return;
        nectorPendingForceApply = false;
        hideMydCartBusy();
        setNectorWanted(false);
        host.dataset.mydNectorReady = '0';
        nectorMountedKey = '';
        // Fall back to static Apply so the shopper can retry.
        host.innerHTML = nectorShellHtml(applicable, false);
        host.hidden = false;
        const retryBtn = host.querySelector('[data-myd-nector-apply]');
        if (retryBtn) {
          retryBtn.disabled = false;
          retryBtn.onclick = (event) => {
            event.preventDefault();
            event.stopPropagation();
            beginNectorApplyFromButton(retryBtn);
          };
        }
        restoreCartScroll(savedScroll);
      });
  }

  function watchNectorApplyResult(opts) {
    const options = opts || {};
    const token = options.token != null ? options.token : nectorMountToken;
    let tries = 0;
    let clicked = false;
    const finish = () => {
      nectorPendingForceApply = false;
      hideMydCartBusy();
      const host = root()?.querySelector('[data-myd-nector-host]');
      if (host) wireNectorApplyButton(host);
    };
    const tick = () => {
      if (token !== nectorMountToken) return;
      if (isNectorDiscountCode(appliedCouponCode)) {
        finish();
        return;
      }
      const host = root()?.querySelector('[data-myd-nector-host]');
      const mount = host?.querySelector('[data-myd-nector-mount]');
      const box =
        mount?.querySelector('input[type="checkbox"]') ||
        host?.querySelector('input[type="checkbox"]');

      if (box && !box.checked && !clicked && tries >= 3) {
        clicked = true;
        box.click();
      }

      fetchCart()
        .then((cart) => {
          if (token !== nectorMountToken) return;
          const code = detectAppliedCode(cart);
          if (isNectorDiscountCode(code)) {
            syncBillFromCart(cart);
            paintCoupons();
            document.dispatchEvent(
              new CustomEvent('cart:updated', { detail: { cart, source: 'myd-nector' } })
            );
            finish();
            return;
          }
          if (tries++ < 60) window.setTimeout(tick, 150);
          else if (options.remountOnce) {
            // First attempt stalled (common right after swapping off GET500) — one clean remount.
            options.remountOnce = false;
            clicked = false;
            tries = 0;
            mountNectorCartWidget({ force: true, forceApply: true });
          } else {
            finish();
          }
        })
        .catch(() => {
          if (tries++ < 60) window.setTimeout(tick, 150);
          else finish();
        });
    };
    window.setTimeout(tick, 200);
  }

  function beginNectorApplyFromButton(applyBtn) {
    setNectorWanted(true);
    nectorPendingForceApply = true;
    showMydCartBusy();
    if (applyBtn) {
      applyBtn.disabled = true;
      applyBtn.textContent = 'Applying…';
    }
    if (nectorScheduleTimer) {
      clearTimeout(nectorScheduleTimer);
      nectorScheduleTimer = null;
    }

    const redeem = () => {
      const host = root()?.querySelector('[data-myd-nector-host]');
      const box = host?.querySelector('[data-myd-nector-mount] input[type="checkbox"]');
      // Prefer clicking a live checkbox (faster, avoids double-tap after GET500).
      if (box && host?.dataset?.mydNectorReady === '1') {
        if (!box.checked) box.click();
        watchNectorApplyResult({ remountOnce: true, token: nectorMountToken });
        return;
      }
      mountNectorCartWidget({ force: true, forceApply: true });
    };

    // Shopify carts hold one discount code — clear GET500/HIGH5 first so
    // myCoins can replace it instead of silently failing underneath it.
    const shopCode =
      appliedCouponCode && !isNectorDiscountCode(appliedCouponCode) ? appliedCouponCode : '';
    if (!shopCode) {
      redeem();
      return;
    }

    postCartDiscount('')
      .then(() => purgeStickyDiscountViaShopify())
      .then(() => postCartDiscount(''))
      .then((cart) => {
        appliedCouponCode = '';
        lastCouponSavingsPaise = 0;
        lastAppliedCouponMeta = null;
        if (cart && cart.items) {
          syncCouponChrome(cart, '');
          paintCoupons();
          document.dispatchEvent(
            new CustomEvent('cart:updated', { detail: { cart, source: 'myd-nector-strip' } })
          );
        }
        // After clearing a shop coupon the previous Nector iframe/SDK is stale —
        // always do a fresh forceApply mount so the first tap redeems.
        mountNectorCartWidget({ force: true, forceApply: true });
      })
      .catch(() => mountNectorCartWidget({ force: true, forceApply: true }));
  }

  // Nector renders its own checkbox inside the visually-hidden proxy mount —
  // this button is the real, visible control and just proxies clicks to it.
  function wireNectorApplyButton(host) {
    const applyBtn = host.querySelector('[data-myd-nector-apply]');
    const mount = host.querySelector('[data-myd-nector-mount]');
    if (!applyBtn || !mount) return;

    const findCheckbox = () =>
      mount.querySelector('.myd-cart__nector-checkbox input[type="checkbox"]') ||
      host.querySelector('input[type="checkbox"]');

    const syncState = () => {
      // Only show Remove when a NECC… code is actually on the cart — Nector's
      // checkbox can look checked while GET500 is still the live Shopify code.
      const applied = isNectorDiscountCode(appliedCouponCode);
      applyBtn.classList.toggle('is-applied', applied && !nectorPendingRemove);
      applyBtn.classList.toggle('is-removing', !!nectorPendingRemove);
      if (nectorPendingRemove) {
        applyBtn.textContent = 'Removing…';
        applyBtn.disabled = true;
        applyBtn.setAttribute('aria-label', 'Removing myCoins');
      } else if (applied) {
        applyBtn.textContent = 'Remove';
        applyBtn.disabled = false;
        applyBtn.setAttribute('aria-label', 'Remove myCoins');
      } else {
        applyBtn.textContent = nectorPendingForceApply ? 'Applying…' : 'Apply';
        applyBtn.disabled = !!nectorPendingForceApply;
        applyBtn.removeAttribute('aria-label');
      }
    };

    applyBtn.onclick = (event) => {
      event.preventDefault();
      event.stopPropagation();
      const currentlyApplied = isNectorDiscountCode(appliedCouponCode);

      if (currentlyApplied) {
        // Opt out — block future NECC re-applies and purge sticky cookie.
        // Do NOT toggle the Nector checkbox — that can race and re-pin NECC….
        nectorPendingForceApply = false;
        nectorPendingRemove = true;
        setNectorWanted(false);
        applyBtn.disabled = true;
        applyBtn.classList.add('is-removing');
        applyBtn.classList.remove('is-applied');
        applyBtn.textContent = 'Removing…';
        showMydCartBusy();
        uncheckNectorCheckbox();
        const finishRemove = () => {
          nectorPendingRemove = false;
          hideMydCartBusy();
          // Soft reset the button only — remounting the card flashes for a second.
          const host = root()?.querySelector('[data-myd-nector-host]');
          const btn = host?.querySelector('[data-myd-nector-apply]');
          if (btn && host) {
            const mount = host.querySelector('[data-myd-nector-mount]');
            if (mount) mount.innerHTML = '';
            nectorMountedKey = '';
            host.dataset.mydNectorReady = '0';
            btn.classList.remove('is-applied', 'is-removing');
            btn.disabled = false;
            btn.textContent = 'Apply';
            btn.removeAttribute('aria-label');
            btn.onclick = (e) => {
              e.preventDefault();
              e.stopPropagation();
              beginNectorApplyFromButton(btn);
            };
          } else {
            schedulePaintNector({ force: true });
          }
        };
        stripDiscountFully()
          .then(() => {
            appliedCouponCode = '';
            syncState();
            return fetchCart()
              .then((cart) => {
                // If sticky cookie resurrected NECC… mid-strip, force clear chrome.
                if (isNectorDiscountCode(detectAppliedCode(cart))) {
                  return stripDiscountFully().then((c2) => c2 || cart);
                }
                return cart;
              })
              .then((cart) => {
                appliedCouponCode = '';
                lastCouponSavingsPaise = 0;
                lastAppliedCouponMeta = null;
                if (cart && cart.items) {
                  syncBillFromCart(cart);
                  if (isNectorDiscountCode(appliedCouponCode)) appliedCouponCode = '';
                  paintCoupons();
                  document.dispatchEvent(
                    new CustomEvent('cart:updated', {
                      detail: { cart, source: 'myd-nector-strip' },
                    })
                  );
                }
              });
          })
          .catch(() => null)
          .finally(finishRemove);
        return;
      }

      beginNectorApplyFromButton(applyBtn);
    };

    syncState();
    if (nectorApplySyncTimer) clearInterval(nectorApplySyncTimer);
    nectorApplySyncTimer = window.setInterval(syncState, 400);
  }

  document.addEventListener('cartdrawer:opened', bootCoupons);
  document.addEventListener('cartdrawer:opened', () => {
    enforceSuppressedNectorGone().finally(() => schedulePaintNector({ force: true }));
  });
  document.addEventListener('cart:updated', (event) => {
    const cart = event?.detail?.cart;
    const source = event?.detail?.source || '';
    if (source === 'myd-nector-strip') return;
    if (cart && cart.items) {
      syncBillFromCart(cart);
    }
    requestAnimationFrame(bootCoupons);
    // Do not remount on every cart update — that loops with Nector /discount/ apply.
    // Remount only when cart HTML was replaced and host was wiped (handled in refreshCartAfterNectorDiscount).
    if (source === 'myd-nector') return;
    const host = root()?.querySelector('[data-myd-nector-host]');
    if (host && !host.querySelector('[data-myd-nector-mount]')) {
      schedulePaintNector({ autoApply: false, force: true });
    }
  });
  document.addEventListener('DOMContentLoaded', () => {
    bootCoupons();
    enforceSuppressedNectorGone();
    if (isMydCartPage()) {
      syncMydCartPageLayout();
      armMilestoneObserver();
      bootMilestones(true);
      document.dispatchEvent(new CustomEvent('cartdrawer:opened'));
    }
  });
  bootCoupons();
  enforceSuppressedNectorGone();
  // Nector paints only on cart open via requestIdleCallback — never blocks drawer boot.

  /* Store credit — "Apply at checkout" explains usage via toast */
  (function bindStoreCreditInfoToast() {
    if (document.documentElement.dataset.mydStoreCreditToast === '1') return;
    document.documentElement.dataset.mydStoreCreditToast = '1';

    const TITLE = 'Store credit';
    const MESSAGE =
      'You will be able to use this store credit balance during checkout.';
    const ICON =
      '<span class="myd-cart__toast-icon" aria-hidden="true">' +
      '<svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">' +
      '<circle cx="12" cy="12" r="9" stroke="currentColor" stroke-width="1.8"/>' +
      '<path d="M12 10.5v5.2" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>' +
      '<circle cx="12" cy="7.6" r="1.05" fill="currentColor"/>' +
      '</svg></span>';
    let hideTimer = null;
    let clearTimer = null;

    function isCartPageContext(cartRoot) {
      return !!(
        cartRoot?.classList?.contains('myd-cart--page') ||
        cartRoot?.closest?.('[data-myd-cart-page], .myd-cart-page__host') ||
        document.querySelector('[data-myd-cart-page]')
      );
    }

    function ensureToast(cartRoot) {
      const onPage = isCartPageContext(cartRoot);
      let toast = document.querySelector('[data-myd-cart-toast]');
      if (!toast) {
        toast = document.createElement('div');
        toast.className = 'myd-cart__toast';
        toast.setAttribute('data-myd-cart-toast', '');
        toast.setAttribute('role', 'status');
        toast.setAttribute('aria-live', 'polite');
        toast.hidden = true;
        toast.innerHTML =
          ICON +
          '<span class="myd-cart__toast-copy">' +
          '<strong class="myd-cart__toast-title"></strong>' +
          '<em class="myd-cart__toast-text"></em>' +
          '</span>';
      }

      toast.classList.toggle('myd-cart__toast--page', onPage);

      if (onPage) {
        // Fixed toast — cart page shell is a CSS grid; an in-flow banner
        // after the hidden header never shows near Store Credit.
        if (toast.parentElement !== document.body) {
          document.body.appendChild(toast);
        }
        return toast;
      }

      if (!cartRoot) return toast;
      const header = cartRoot.querySelector('.myd-cart__header');
      if (header && header.parentNode) {
        if (toast.previousElementSibling !== header) {
          header.insertAdjacentElement('afterend', toast);
        }
      } else {
        const shell = cartRoot.querySelector('.myd-cart__shell');
        if (shell && toast.parentElement !== shell) shell.prepend(toast);
        else if (!shell && toast.parentElement !== cartRoot) cartRoot.appendChild(toast);
      }
      return toast;
    }

    function showToast(cartRoot) {
      const toast = ensureToast(cartRoot);
      if (!toast) return;
      const titleEl = toast.querySelector('.myd-cart__toast-title');
      const textEl = toast.querySelector('.myd-cart__toast-text');
      if (titleEl) titleEl.textContent = TITLE;
      if (textEl) textEl.textContent = MESSAGE;
      toast.hidden = false;
      toast.classList.remove('is-visible');
      void toast.offsetWidth;
      toast.classList.add('is-visible');
      if (hideTimer) clearTimeout(hideTimer);
      if (clearTimer) clearTimeout(clearTimer);
      hideTimer = setTimeout(() => {
        toast.classList.remove('is-visible');
        clearTimer = setTimeout(() => {
          toast.hidden = true;
        }, 320);
      }, 3600);
    }

    document.addEventListener(
      'click',
      (event) => {
        const btn = event.target?.closest?.('[data-myd-store-credit-info]');
        if (!btn) return;
        event.preventDefault();
        event.stopPropagation();
        const cartRoot =
          btn.closest('[data-myd-cart]') || document.querySelector('[data-myd-cart]');
        showToast(cartRoot);
      },
      true
    );
  })();

  /* iOS: finger on .myd-cart__yml-scroller (overflow-x) never scrolls the cart
     body vertically. Forward vertical pans to .myd-cart__body; leave horizontal
     to the scroller (touch-action: pan-x). */
  (function bindYmlVerticalPassthrough() {
    if (document.documentElement.dataset.mydYmlPassthrough === '1') return;
    document.documentElement.dataset.mydYmlPassthrough = '1';

    // Raw 1:1 finger-tracked scrollTop (below) has no coast/deceleration once
    // the finger lifts, unlike native scroll — that's what read as "slow,
    // takes more effort" versus scrolling the rest of the cart body. This
    // adds the same momentum-with-friction feel on release.
    const FRICTION = 0.94;
    const MIN_VELOCITY = 0.02;
    let state = null;
    let momentumRaf = null;

    function stopMomentum() {
      if (momentumRaf) cancelAnimationFrame(momentumRaf);
      momentumRaf = null;
    }

    function runMomentum(body, velocity, lastTime) {
      momentumRaf = requestAnimationFrame((now) => {
        const dt = Math.min(48, now - lastTime) || 16;
        body.scrollTop += velocity * dt;
        velocity *= FRICTION;
        if (Math.abs(velocity) < MIN_VELOCITY || !body.isConnected) {
          momentumRaf = null;
          return;
        }
        runMomentum(body, velocity, now);
      });
    }

    document.addEventListener(
      'touchstart',
      (event) => {
        const target = event.target;
        if (!target || typeof target.closest !== 'function') return;
        const scroller = target.closest('.myd-cart__yml-scroller');
        if (!scroller || !scroller.closest('[data-myd-cart]')) return;
        if (!event.touches || event.touches.length !== 1) return;
        stopMomentum();
        const touch = event.touches[0];
        state = {
          scroller,
          body: scroller.closest('.myd-cart__body'),
          startX: touch.clientX,
          startY: touch.clientY,
          lastY: touch.clientY,
          lastTime: event.timeStamp,
          velocity: 0,
          axis: null,
        };
      },
      { passive: true, capture: true }
    );

    document.addEventListener(
      'touchmove',
      (event) => {
        if (!state || !event.touches || event.touches.length !== 1) return;
        if (!state.scroller.isConnected) {
          state = null;
          return;
        }
        const touch = event.touches[0];
        const dx = touch.clientX - state.startX;
        const dy = touch.clientY - state.startY;
        if (!state.axis) {
          if (Math.abs(dx) < 8 && Math.abs(dy) < 8) return;
          state.axis = Math.abs(dy) > Math.abs(dx) ? 'y' : 'x';
        }
        if (state.axis !== 'y' || !state.body) return;
        const dt = Math.max(1, event.timeStamp - state.lastTime);
        const delta = state.lastY - touch.clientY;
        state.body.scrollTop += delta;
        // Smoothed instantaneous velocity (px/ms) for the release-momentum animation.
        state.velocity = state.velocity * 0.72 + (delta / dt) * 0.28;
        state.lastY = touch.clientY;
        state.lastTime = event.timeStamp;
        event.preventDefault();
      },
      { passive: false, capture: true }
    );

    const end = (event) => {
      if (state && state.axis === 'y' && state.body && Math.abs(state.velocity) > MIN_VELOCITY) {
        runMomentum(state.body, state.velocity, event.timeStamp || performance.now());
      }
      state = null;
    };
    document.addEventListener('touchend', end, { passive: true, capture: true });
    document.addEventListener('touchcancel', end, { passive: true, capture: true });
  })();

  function formatRupees(paise) {
    const n = Math.max(0, Math.round(Number(paise) || 0) / 100);
    let out;
    try {
      out = new Intl.NumberFormat('en-IN', {
        style: 'currency',
        currency: 'INR',
        maximumFractionDigits: 0,
      }).format(n);
    } catch (e) {
      out = '₹' + n.toLocaleString('en-IN');
    }
    return String(out).replace(/\u00a0/g, ' ').replace('₹ ', '₹').trim();
  }

  function formatRupeesTight(paise) {
    return formatRupees(paise);
  }

  function stepLabel(step) {
    if (step.label) return String(step.label);
    if (step.title) return String(step.title);
    const disc = Number(step.discount_value ?? step.discount ?? 0);
    if (disc > 0) return `₹${disc} OFF`;
    return '';
  }

  function readMilestoneConfig(host) {
    if (!host) return null;

    const b64 = host.getAttribute('data-milestone-b64');
    if (b64) {
      try {
        const json = decodeURIComponent(
          Array.prototype.map
            .call(atob(b64), (c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
            .join('')
        );
        return JSON.parse(json);
      } catch (e) {
        try {
          return JSON.parse(atob(b64));
        } catch (e2) {
          /* fall through */
        }
      }
    }

    const area = host.querySelector('[data-myd-milestone-json]');
    if (area?.value || area?.textContent) {
      try {
        return JSON.parse(String(area.value || area.textContent || '').trim());
      } catch (e) {
        /* fall through */
      }
    }

    const attr = host.getAttribute('data-app-stepper');
    if (attr != null && String(attr).trim() !== '') {
      try {
        return {
          cartTotal: Number(host.dataset.cartTotal || 0),
          productQty: Number(host.dataset.productQty || 0),
          app_stepper: JSON.parse(attr),
        };
      } catch (e) {
        /* fall through */
      }
    }

    const cfgEl = document.getElementById('MydCartMilestoneData');
    if (!cfgEl) return null;
    try {
      return JSON.parse(cfgEl.textContent || '{}');
    } catch (e) {
      return null;
    }
  }

  function decodeStepperDeep(raw, depth = 0) {
    if (depth > 4 || raw == null) return [];
    if (typeof raw === 'string') {
      const trimmed = raw.trim();
      if (!trimmed) return [];
      try {
        return decodeStepperDeep(JSON.parse(trimmed), depth + 1);
      } catch (e) {
        return [];
      }
    }
    if (Array.isArray(raw)) return raw;
    if (typeof raw === 'object') {
      const nested =
        raw.steps ||
        raw.milestones ||
        raw.tiers ||
        raw.app_stepper ||
        raw.data ||
        raw.items ||
        raw.values;
      if (nested != null) return decodeStepperDeep(nested, depth + 1);
    }
    return [];
  }

  function normalizeStepper(raw) {
    return decodeStepperDeep(raw);
  }

  function stepThresholdPaise(step) {
    let thr = step.threshold ??
      step.min_amount ??
      step.amount ??
      step.min ??
      step.cart_value ??
      step.min_cart_value ??
      step.order_value ??
      step.value ??
      step.price ??
      null;
    if (typeof thr === 'string') {
      thr = thr.replace(/[^\d.]/g, '');
    }
    thr = Number(thr);
    if (!Number.isFinite(thr) || thr <= 0) return 0;
    // Values like 9999 are rupees; very large values are already paise
    return thr > 100000 ? thr : thr * 100;
  }

  function fallbackQtySteps() {
    return [
      { _paise: 0, _label: 'Get ₹100 OFF', _qty: 3, _mode: 'qty' },
      { _paise: 0, _label: 'Get ₹200 OFF', _qty: 5, _mode: 'qty' },
      { _paise: 0, _label: 'Get ₹300 OFF', _qty: 7, _mode: 'qty' },
    ];
  }

  function paintMilestones() {
    const host = document.querySelector('[data-myd-tiers][data-source="metaobject-stepper"]');
    if (!host) return;

    const cfg = readMilestoneConfig(host) || {
      cartTotal: Number(host.dataset.cartTotal || 0),
      productQty: Number(host.dataset.productQty || 0),
      app_stepper: [],
    };

    let steps = normalizeStepper(cfg.app_stepper)
      .map((step) => ({
        ...step,
        _paise: stepThresholdPaise(step),
        _label: stepLabel(step),
        _mode: 'money',
      }))
      .filter((s) => s._paise > 0)
      .sort((a, b) => a._paise - b._paise);

    const cartTotal = Number(cfg.cartTotal ?? host.dataset.cartTotal ?? 0);
    const productQty = Number(cfg.productQty ?? host.dataset.productQty ?? 0);
    const msg = host.querySelector('[data-myd-tier-msg]');
    const fill = host.querySelector('[data-myd-tier-fill]');
    const nodes = host.querySelector('[data-myd-tier-nodes]');
    const rail = host.querySelector('.myd-cart__tiers-rail');

    // If metaobject JSON is missing/unreadable, still show the qty fallback rail
    let usingQtyFallback = false;
    if (!steps.length) {
      steps = fallbackQtySteps();
      usingQtyFallback = true;
    }

    let current = null;
    let next = null;
    let reachedIdx = -1;

    if (usingQtyFallback) {
      for (let i = 0; i < steps.length; i++) {
        const step = steps[i];
        if (productQty >= step._qty) {
          current = step;
          reachedIdx = i;
        } else if (!next) {
          next = step;
        }
      }
      if (next) {
        const remain = Math.max(0, next._qty - productQty);
        if (msg) {
          msg.textContent = `SHOP ${remain} PRODUCT${remain === 1 ? '' : 'S'} TO ${String(next._label).toUpperCase()}`;
        }
      } else if (current && msg) {
        msg.textContent = `${String(current._label).toUpperCase()} UNLOCKED`;
      } else if (msg) {
        msg.textContent = 'Add items to unlock offers';
      }
    } else {
      for (let i = 0; i < steps.length; i++) {
        const step = steps[i];
        if (cartTotal >= step._paise) {
          current = step;
          reachedIdx = i;
        } else if (!next) {
          next = step;
        }
      }

      if (next) {
        const remain = Math.max(0, next._paise - cartTotal);
        if (msg) {
          msg.textContent = `Add item worth ${formatRupees(remain)} more to avail ${next._label}`;
        }
      } else if (current) {
        if (msg) msg.textContent = `${current._label} unlocked`;
      } else if (msg) {
        msg.textContent = 'Add items to unlock offers';
      }
    }

    // Map fill to node centers on the rail.
    // Drawer: nodes sit in equal columns (centers at (i+0.5)/n), track 8%→92%.
    // /cart page: first/last nodes hug the edges (centers at i/(n-1)), track 0%→100%.
    const n = steps.length;
    const fullRail = isMydCartPage();
    const RAIL_LEFT = fullRail ? 0 : 8;
    const RAIL_RIGHT = fullRail ? 100 : 92;
    const nodeCenter = (index) => {
      if (n <= 1) return 50;
      if (fullRail) return (index / (n - 1)) * 100;
      return ((index + 0.5) / n) * 100;
    };

    let fillEnd = RAIL_LEFT;
    if (usingQtyFallback) {
      if (reachedIdx >= n - 1) {
        fillEnd = RAIL_RIGHT;
      } else if (reachedIdx < 0) {
        const t = Math.min(1, Math.max(0, productQty / steps[0]._qty));
        fillEnd = RAIL_LEFT + (nodeCenter(0) - RAIL_LEFT) * t;
      } else {
        const nextIdx = reachedIdx + 1;
        const prevQty = steps[reachedIdx]._qty;
        const nextQty = steps[nextIdx]._qty;
        const span = Math.max(1, nextQty - prevQty);
        const t = Math.min(1, Math.max(0, (productQty - prevQty) / span));
        const start = nodeCenter(reachedIdx);
        const end = nodeCenter(nextIdx);
        fillEnd = start + (end - start) * t;
      }
    } else if (reachedIdx >= n - 1) {
      fillEnd = RAIL_RIGHT;
    } else if (reachedIdx < 0) {
      const t = Math.min(1, Math.max(0, cartTotal / steps[0]._paise));
      fillEnd = RAIL_LEFT + (nodeCenter(0) - RAIL_LEFT) * t;
    } else {
      const nextIdx = reachedIdx + 1;
      const prevPaise = steps[reachedIdx]._paise;
      const nextPaise = steps[nextIdx]._paise;
      const span = Math.max(1, nextPaise - prevPaise);
      const t = Math.min(1, Math.max(0, (cartTotal - prevPaise) / span));
      const start = nodeCenter(reachedIdx);
      const end = nodeCenter(nextIdx);
      fillEnd = start + (end - start) * t;
    }

    const fillWidth = Math.max(0, Math.min(RAIL_RIGHT - RAIL_LEFT, fillEnd - RAIL_LEFT));
    const fillWidthCss = `${Math.round(fillWidth * 100) / 100}%`;
    const ariaPct = Math.round((fillWidth / (RAIL_RIGHT - RAIL_LEFT)) * 100);

    if (fill && fill.style.width !== fillWidthCss) {
      fill.style.width = fillWidthCss;
    }
    if (rail) rail.setAttribute('aria-valuenow', String(ariaPct));

    if (nodes) {
      const html = steps
        .map((step) => {
          const active = usingQtyFallback
            ? productQty >= step._qty
            : cartTotal >= step._paise;
          const sub = usingQtyFallback
            ? `On ${step._qty} products`
            : `On ₹${Math.round(step._paise / 100).toLocaleString('en-IN')}+`;
          return `<div class="myd-cart__tier-node${active ? ' is-active' : ''}">
            <span class="myd-cart__tier-dot"></span>
            <span class="myd-cart__tier-label">
              <strong>${escapeHtml(step._label)}</strong>
              <em>${escapeHtml(sub)}</em>
            </span>
          </div>`;
        })
        .join('');
      if (nodes.innerHTML !== html) nodes.innerHTML = html;
    }
  }

  function escapeHtml(str) {
    return String(str || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  let paintingMilestones = false;
  let milestonePaintTimer = null;
  let milestoneIgnoreMutationsUntil = 0;
  let lastMilestoneSignature = '';

  function milestoneSignature() {
    const host = document.querySelector('[data-myd-tiers][data-source="metaobject-stepper"]');
    if (!host) return '';
    return `${host.getAttribute('data-milestone-b64') || host.getAttribute('data-app-stepper') || ''}|${host.dataset?.cartTotal || ''}|${host.dataset?.productQty || ''}`;
  }

  function bootMilestones(force) {
    if (paintingMilestones) return;
    const signature = milestoneSignature();
    if (!force && signature && signature === lastMilestoneSignature) return;
    if (milestonePaintTimer) clearTimeout(milestonePaintTimer);
    milestonePaintTimer = setTimeout(() => {
      paintingMilestones = true;
      milestoneIgnoreMutationsUntil = Date.now() + 200;
      try {
        paintMilestones();
        lastMilestoneSignature = milestoneSignature();
      } finally {
        paintingMilestones = false;
      }
    }, 30);
  }

  // Milestones only matter once the drawer is actually open — don't pay the
  // paint/query cost on every page load for a rail the visitor hasn't seen.
  let milestoneObserverArmed = false;

  function armMilestoneObserver() {
    if (milestoneObserverArmed) return;
    milestoneObserverArmed = true;
    const mini = document.getElementById('mini-cart');
    if (!mini) return;
    // mini-cart's fetch path replaces its own direct children wholesale
    // (see cart-drawer.js: `#mini-cart.innerHTML = ...`), so a childList
    // observer on mini-cart itself catches that swap without needing
    // subtree:true — which was firing on every unrelated bill/text update
    // deep inside the drawer (qty changes, coupon panel, etc).
    const mo = new MutationObserver(() => {
      if (paintingMilestones) return;
      if (Date.now() < milestoneIgnoreMutationsUntil) return;
      if (!document.querySelector('[data-myd-tiers][data-source="metaobject-stepper"]')) return;
      bootMilestones(false);
    });
    mo.observe(mini, { childList: true });
  }

  document.addEventListener('cartdrawer:opened', () => {
    armMilestoneObserver();
    bootMilestones(true);
  });
  document.addEventListener('cart:updated', (event) => {
    const source = event?.detail?.source || '';
    if (source === 'myd-nector-strip') return;
    bootMilestones(true);
  });

  /* —— You May Also Like: pad short Shopify recommendations with wishlist, then bestsellers —— */
  function wishlistHandlesForFill() {
    if (window.MYDWishlist && typeof window.MYDWishlist.allHandles === 'function') {
      return window.MYDWishlist.allHandles();
    }
    try {
      const raw = localStorage.getItem('myd_wishlist_v1');
      if (!raw) return [];
      const parsed = JSON.parse(raw);
      const seen = {};
      const out = [];
      (parsed?.boards || []).forEach((board) => {
        (board.items || []).forEach((handle) => {
          if (!handle || seen[handle]) return;
          seen[handle] = true;
          out.push(handle);
        });
      });
      return out;
    } catch (_) {
      return [];
    }
  }

  function cartHandlesInYml(rootEl) {
    const blocked = new Set();
    (rootEl || document)
      .querySelectorAll('.myd-cart__line[data-product-handle]')
      .forEach((line) => {
        const h = line.getAttribute('data-product-handle');
        if (h) blocked.add(h);
      });
    return blocked;
  }

  function formatYmlMoney(cents) {
    const n = Number(cents) || 0;
    if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
      try {
        return window.Shopify.formatMoney(n);
      } catch (_) {
        /* fall through */
      }
    }
    const rupees = Math.round(n / 100);
    return `₹${rupees.toLocaleString('en-IN')}`;
  }

  function productImageFromJs(product) {
    const src = product?.featured_image || product?.images?.[0];
    if (!src) return '';
    if (typeof src === 'string') {
      return src.startsWith('//') ? `https:${src}` : src;
    }
    if (src.src) {
      return src.src.startsWith('//') ? `https:${src.src}` : src.src;
    }
    return '';
  }

  function firstAvailableVariantJs(product) {
    const variants = product?.variants || [];
    return variants.find((v) => v.available) || variants[0] || null;
  }

  function buildWishlistYmlCard(product) {
    const handle = product.handle;
    const url = product.url || `/products/${handle}`;
    const variant = firstAvailableVariantJs(product);
    const soldOut = product.available === false || !variant || variant.available === false;
    const imgUrl = productImageFromJs(product);
    const compare = product.compare_at_price || 0;
    const price = product.price || 0;
    let discountPct = 0;
    if (compare > price && compare > 0) {
      discountPct = Math.round(((compare - price) * 100) / compare);
    }

    const card = document.createElement('article');
    card.className = `myd-search-card myd-cart__yml-card${soldOut ? ' is-sold-out' : ''}`;
    card.setAttribute('data-product-handle', handle);
    card.setAttribute('data-product-id', String(product.id || ''));
    if (variant?.id) card.setAttribute('data-variant-id', String(variant.id));
    card.setAttribute('data-myd-yml-fill-source', 'wishlist');

    const media = document.createElement('div');
    media.className = 'myd-search-card__media';
    const mediaLink = document.createElement('a');
    mediaLink.className = 'myd-search-card__media-link';
    mediaLink.href = url;
    mediaLink.setAttribute('aria-label', product.title || handle);
    if (imgUrl) {
      const img = document.createElement('img');
      img.className = 'myd-search-card__image myd-search-card__image--primary';
      let src = imgUrl;
      if (src.startsWith('//')) src = `https:${src}`;
      if (src.includes('cdn/shop') && !/[?&]width=/.test(src)) {
        src = src.includes('?') ? `${src}&width=360` : `${src}?width=360`;
      }
      img.src = src;
      img.alt = product.title || '';
      img.loading = 'lazy';
      img.width = 360;
      img.height = 450;
      img.decoding = 'async';
      mediaLink.appendChild(img);
    }
    media.appendChild(mediaLink);

    if (!soldOut) {
      const wishlisted = document.createElement('div');
      wishlisted.className = 'myd-search-card__wishlisted';
      wishlisted.textContent = 'Wishlisted';
      media.appendChild(wishlisted);
      if (discountPct >= 1) {
        const off = document.createElement('div');
        off.className = 'myd-search-card__discount';
        off.textContent = `${discountPct}% OFF`;
        media.appendChild(off);
      }
    }

    const body = document.createElement('div');
    body.className = 'myd-search-card__body';
    const typeRow = document.createElement('div');
    typeRow.className = 'myd-search-card__type-row';
    if (product.type) {
      const typeEl = document.createElement('p');
      typeEl.className = 'myd-search-card__type';
      typeEl.textContent = product.type;
      typeRow.appendChild(typeEl);
    }
    body.appendChild(typeRow);

    const titleLink = document.createElement('a');
    titleLink.href = url;
    titleLink.className = 'myd-search-card__info';
    const title = document.createElement('h3');
    title.className = 'myd-search-card__title';
    title.textContent = product.title || handle;
    titleLink.appendChild(title);
    body.appendChild(titleLink);

    const priceRow = document.createElement('div');
    priceRow.className = 'myd-search-card__price';
    const current = document.createElement('span');
    current.className = 'myd-search-card__price-current';
    current.textContent = formatYmlMoney(price);
    priceRow.appendChild(current);
    if (compare > price) {
      const was = document.createElement('span');
      was.className = 'myd-search-card__price-compare';
      was.textContent = formatYmlMoney(compare);
      priceRow.appendChild(was);
    }
    body.appendChild(priceRow);

    const atc = document.createElement('button');
    atc.type = 'button';
    atc.className = 'myd-search-card__view-details';
    atc.setAttribute('data-myd-quick-add', '');
    atc.setAttribute('data-product-handle', handle);
    atc.textContent = soldOut ? 'Sold Out' : 'Add to cart';
    if (soldOut) {
      atc.disabled = true;
      atc.setAttribute('aria-disabled', 'true');
      atc.setAttribute('data-sold-out', 'true');
    } else {
      atc.setAttribute('aria-disabled', 'false');
      atc.setAttribute('data-sold-out', 'false');
    }

    card.appendChild(media);
    card.appendChild(body);
    card.appendChild(atc);
    return card;
  }

  async function fetchProductForYml(handle) {
    try {
      const res = await fetch(`/products/${handle}.js`, { credentials: 'same-origin' });
      if (!res.ok) return null;
      return await res.json();
    } catch (_) {
      return null;
    }
  }

  function createYmlSkeletonCard() {
    const el = document.createElement('div');
    el.className = 'myd-cart__yml-skel';
    el.setAttribute('aria-hidden', 'true');
    el.innerHTML =
      '<div class="myd-cart__yml-skel-media"></div>' +
      '<div class="myd-cart__yml-skel-body">' +
      '<span class="myd-cart__yml-skel-line myd-cart__yml-skel-line--type"></span>' +
      '<span class="myd-cart__yml-skel-line myd-cart__yml-skel-line--title"></span>' +
      '<span class="myd-cart__yml-skel-line myd-cart__yml-skel-line--price"></span>' +
      '<span class="myd-cart__yml-skel-line myd-cart__yml-skel-line--cta"></span>' +
      '</div>';
    return el;
  }

  function ensureYmlSkeletonPads(scroller, limit) {
    if (!scroller) return;
    const realCount = scroller.querySelectorAll('.myd-cart__yml-card, .myd-search-card').length;
    let skelCount = scroller.querySelectorAll('.myd-cart__yml-skel').length;
    while (realCount + skelCount < limit) {
      scroller.appendChild(createYmlSkeletonCard());
      skelCount += 1;
    }
  }

  function replaceYmlSkeleton(scroller, node) {
    const skel = scroller?.querySelector('.myd-cart__yml-skel');
    if (skel) {
      skel.replaceWith(node);
      return;
    }
    scroller?.appendChild(node);
  }

  function clearYmlSkeletons(rootEl) {
    rootEl?.querySelectorAll?.('.myd-cart__yml-skel')?.forEach((el) => el.remove());
    rootEl?.classList?.remove('is-yml-loading');
  }

  const ymlFillInflight = new WeakMap();

  async function fillMydCartYml(host) {
    const rootEl = host || document.querySelector('cart-recommendations[data-myd-yml-fill]');
    if (!rootEl || !rootEl.hasAttribute('data-myd-yml-fill')) return;

    const inflight = ymlFillInflight.get(rootEl);
    if (inflight) return inflight;

    const run = (async () => {
      const attrLimit = Math.max(1, Number(rootEl.getAttribute('data-fill-limit')) || 4);
      // /cart always fills to 6 so desktop can show a full single row.
      const limit = isMydCartPage() ? Math.max(attrLimit, 6) : attrLimit;
      const scroller =
        rootEl.querySelector('[data-myd-yml-scroller]') ||
        rootEl.querySelector('.myd-cart__yml-scroller');
      if (!scroller) return;

      const existing = () =>
        [...scroller.querySelectorAll('.myd-cart__yml-card, .myd-search-card')];
      let cards = existing();
      if (cards.length >= limit) {
        clearYmlSkeletons(rootEl);
        return;
      }

      rootEl.dataset.mydYmlFilling = '1';
      rootEl.classList.add('is-yml-loading');
      ensureYmlSkeletonPads(scroller, limit);
      try {
        const blocked = cartHandlesInYml(document);
        cards.forEach((card) => {
          const h = card.getAttribute('data-product-handle');
          if (h) blocked.add(h);
        });

        // 1) Wishlist fills first
        const wishHandles = wishlistHandlesForFill().filter((h) => !blocked.has(h));
        for (const handle of wishHandles) {
          if (existing().length >= limit) break;
          if (blocked.has(handle)) continue;
          const product = await fetchProductForYml(handle);
          if (!product || product.available === false) continue;
          blocked.add(handle);
          replaceYmlSkeleton(scroller, buildWishlistYmlCard(product));
        }

        // 2) Bestsellers (server-rendered pool) for any remaining gaps
        cards = existing();
        if (cards.length < limit) {
          const pool = rootEl.querySelector('template[data-myd-yml-fill-pool]');
          if (pool?.content) {
            const clones = [...pool.content.querySelectorAll('.myd-cart__yml-card, .myd-search-card')];
            for (const node of clones) {
              if (existing().length >= limit) break;
              const handle = node.getAttribute('data-product-handle');
              if (!handle || blocked.has(handle)) continue;
              blocked.add(handle);
              replaceYmlSkeleton(scroller, node.cloneNode(true));
            }
          }
        }

        document.dispatchEvent(
          new CustomEvent('myd:yml-init', { bubbles: true, detail: { root: rootEl } })
        );
      } finally {
        clearYmlSkeletons(rootEl);
        delete rootEl.dataset.mydYmlFilling;
        ymlFillInflight.delete(rootEl);
      }
    })();

    ymlFillInflight.set(rootEl, run);
    return run;
  }

  window.MYDCartFillYml = fillMydCartYml;
  document.addEventListener('myd:yml-recommendations', (event) => {
    fillMydCartYml(event.detail?.root || event.target);
  });
})();
